import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Award,
  Calendar,
  CalendarCheck,
  Check,
  ChevronRight,
  Compass,
  Copy,
  Eye,
  Film,
  Flame,
  Gauge,
  Book,
  Car,
  Crown,
  Frown,
  Globe,
  Grid,
  HelpCircle,
  Home,
  Image as ImageIcon,
  Languages,
  Lock,
  LogIn,
  Mountain,
  Map as MapIcon,
  MapPin,
  MapPinCheck,
  Menu,
  Minimize2,
  Newspaper,
  Pencil,
  Plane,
  Play,
  Plus,
  Quote,
  Radio,
  RefreshCw,
  RotateCcw,
  ScanSearch,
  Scroll,
  Search,
  Send,
  Settings,
  Snowflake,
  Sparkles,
  Swords,
  Target,
  Telescope,
  Timer,
  Trophy,
  User,
  Users,
  Volume2,
  VolumeX,
  X,
  Zap,
  ZoomIn,
  ZoomOut,
  createIcons
} from "lucide";
import {
  buildDuelShareUrl,
  claimUsername,
  clearDuelCodeFromUrl,
  compareDuel,
  continueSession,
  createDuel,
  createLeague,
  ensurePlayer,
  fetchCollection,
  fetchAtlasProgress,
  fetchEvents,
  fetchGlobalStats,
  fetchMissions,
  fetchRoundScoreboard,
  fetchTickerEntries,
  startMission,
  fetchLeagueDetail,
  fetchMyLeagues,
  fetchPersonalStats,
  fetchReplay,
  fetchUsername,
  fetchWeeklyRecap,
  getDailyStatus,
  getDisplayName,
  getDuel,
  getLeaderboard,
  getSession,
  joinLeague,
  listAchievements,
  readDuelCodeFromUrl,
  revealRound,
  setDisplayName,
  startSession,
  submitEmailScore,
  takeHint as takeHintApi
} from "./services/api";
import { ERA_FILTERS, TIMELINE_RANGES } from "./data/rounds";
import { SPLASH_SCENES, publicIdFromSplashUrl, type SplashScene } from "./data/splash.generated";
import { formatDistance, formatYear, formatYearsOff, parseYearInput } from "./lib/format";
import { composeRevealBanter } from "./lib/banter";
import type {
  DailyMeta,
  DuelOpponent,
  DuelSummary,
  Era,
  EraFilter,
  Guess,
  HintPayload,
  HintType,
  LeaderboardRegion,
  MissionSummary,
  PublicRound,
  RevealPayload,
  RoundScoreboard,
  SessionMeta,
  SessionMode,
  TickerEntry
} from "./types";
import { feedback } from "./services/feedback";
import { ensurePushSubscription, registerServiceWorker } from "./services/pwa";
import { GuessMap } from "./ui/guessMap";
import { HotspotOverlay } from "./ui/hotspots";
import { PanoramaViewer } from "./ui/panoramaViewer";
import { TimelineControl } from "./ui/timeline";
import { AchievementToastQueue } from "./ui/achievementToast";
import { animateNumber, celebrateScore } from "./ui/celebrations";
import { renderReplayVideo } from "./ui/replayVideo";
import { renderShareCard } from "./ui/shareCard";
import "./styles.css";

const ROUND_DURATION_MS_DEFAULT = 100_000;
const ROUND_DURATION_MS_QUICKPLAY = 30_000;
const DEFAULT_FILTER: EraFilter = "all";
const AUDIO_SETTING_KEY = "latlongago.audio";
const IRON_LABELS_KEY = "latlongago.iron.labels";
const IRON_ERA_KEY = "latlongago.iron.era";
const IRON_HINTS_KEY = "latlongago.iron.hints";
const ACTIVE_SESSION_KEY = "latlongago.activeSession";
const MOBILE_SPLASH_SCENE_COUNT = 2;
const DESKTOP_SPLASH_SCENE_COUNT = 5;

// Persist the in-progress session id so a refresh / accidental tab close
// resumes at the round the player was on instead of starting over. Modes
// with predetermined round lists (daily/duel/event/mission) are also safe
// to resume since the server already tracks how many rounds were completed.
function rememberActiveSession(sessionId: string): void {
  try {
    window.localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
  } catch {
    /* private mode / quota — ignore */
  }
}

function clearActiveSession(): void {
  try {
    window.localStorage.removeItem(ACTIVE_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function readActiveSession(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_SESSION_KEY);
  } catch {
    return null;
  }
}

function isLikelyMobileViewport(): boolean {
  return window.matchMedia?.("(max-width: 767px), (pointer: coarse)").matches ?? window.innerWidth <= 767;
}
const THEME_AUDIO_URLS = [
  "/audio-theme-song/beyond_the_hearth_fire.mp3",
  "/audio-theme-song/breath_of_the_high_plateau.mp3",
  "/audio-theme-song/oars_against_the_morning_tide.mp3",
  "/audio-theme-song/rite_of_the_morning_fire.mp3",
  "/audio-theme-song/stone_city_awakening.mp3",
  "/audio-theme-song/the_hearth_at_daybreak.mp3",
  "/audio-theme-song/trails_of_the_first_tribe.mp3"
];
const REGIONS: Array<{ id: LeaderboardRegion; label: string }> = [
  { id: "global", label: "Global" },
  { id: "europe", label: "Europe" },
  { id: "americas", label: "Americas" },
  { id: "asia", label: "Asia" },
  { id: "africa", label: "Africa" },
  { id: "oceania", label: "Oceania" }
];

type GameState = {
  selectedFilter: EraFilter;
  selectedMode: SessionMode;
  selectedRegion: LeaderboardRegion;
  duelCodeInput: string;
  pendingDuel: DuelSummary | null;
  daily: DailyMeta | null;
  session: SessionMeta | null;
  currentRoundIndex: number;
  currentRound: PublicRound | null;
  guess: Guess;
  phase: "year" | "location";
  lastSubmissionTimedOut: boolean;
  revealed: boolean;
  timerStart: number;
  timerDeadline: number;
  timerFrame: number | null;
  timerDurationMs: number;
  revealPanelTimeout: number | null;
  introTimeout: number | null;
  introActive: boolean;
  audioEnabled: boolean;
  themeAudioUrl: string;
  lastReveal: RevealPayload | null;
  perRound: Array<{ reveal: RevealPayload; guess: Guess }>;
  duelOpponent: DuelOpponent | null;
  duelShareCode: string | null;
  // Per-roundId map of the opponent's actual guess in a duel — populated
  // once when a duel session begins and used to drop their pin at reveal.
  duelOpponentGuesses: Record<string, { lat: number | null; lng: number | null; year: number; score: number }>;
  ironMode: { mapLabelsHidden: boolean; eraStampHidden: boolean; hintsDisabled: boolean };
  detectivePhase: 1 | 2 | 3 | 4;
};

type AudioPlayOptions = {
  disableOnFailure?: boolean;
  fadeInMs?: number;
};

const icons = {
  Award,
  Book,
  Calendar,
  CalendarCheck,
  Car,
  Check,
  ChevronRight,
  Compass,
  Copy,
  Crown,
  Eye,
  Film,
  Flame,
  Frown,
  Gauge,
  Globe,
  Grid,
  HelpCircle,
  Home,
  Image: ImageIcon,
  Languages,
  Lock,
  LogIn,
  Mountain,
  Map: MapIcon,
  MapPin,
  MapPinCheck,
  Menu,
  Minimize2,
  Newspaper,
  Pencil,
  Plane,
  Play,
  Plus,
  Quote,
  Radio,
  RefreshCw,
  RotateCcw,
  ScanSearch,
  Scroll,
  Search,
  Send,
  Settings,
  Snowflake,
  Sparkles,
  Swords,
  Target,
  Telescope,
  Timer,
  Trophy,
  User,
  Users,
  Volume2,
  VolumeX,
  X,
  Zap,
  ZoomIn,
  ZoomOut
};

createIcons({ icons });

const elements = {
  viewer: requiredElement("#panorama-viewer"),
  roundTitle: requiredElement("#round-title"),
  timerLabel: requiredElement("#timer-label"),
  scoreLabel: requiredElement("#score-label"),
  audioToggle: requiredElement<HTMLButtonElement>("#audio-toggle"),
  roundIndex: requiredElement("#round-index"),
  roundDots: requiredElement("#round-dots"),
  submitButton: requiredElement<HTMLButtonElement>("#submit-button"),
  nextButton: requiredElement<HTMLButtonElement>("#next-button"),
  nextButtonText: requiredElement("#next-button span"),
  resultPanel: requiredElement("#result-panel"),
  distanceResult: requiredElement("#distance-result"),
  distanceTag: requiredElement("#distance-tag"),
  yearResult: requiredElement("#year-result"),
  yearTag: requiredElement("#year-tag"),
  timeResult: requiredElement("#time-result"),
  timeTag: requiredElement("#time-tag"),
  roundScoreResult: requiredElement("#round-score-result"),
  bonusTag: requiredElement("#bonus-tag"),
  revealBanter: requiredElement("#reveal-banter"),
  gameStage: requiredElement(".game-stage"),
  startCue: requiredElement("#start-cue"),
  answerTitle: requiredElement("#answer-title"),
  answerMeta: requiredElement("#answer-meta"),
  answerSummary: requiredElement("#answer-summary"),
  answerLink: requiredElement<HTMLAnchorElement>("#answer-link"),
  mapReset: requiredElement<HTMLButtonElement>("#map-reset"),
  timelineCanvas: requiredElement<HTMLCanvasElement>("#timeline-canvas"),
  yearEventTooltip: requiredElement("#year-event-tooltip"),
  eraStamp: requiredElement("#era-stamp"),
  globalAvgLine: requiredElement("#global-avg-line"),
  endRecap: requiredElement("#end-recap"),
  timelineYear: requiredElement<HTMLButtonElement>("#timeline-year"),
  timelineYearInput: requiredElement<HTMLInputElement>("#timeline-year-input"),
  textStage: requiredElement("#text-stage"),
  textEyebrow: requiredElement("#text-eyebrow"),
  textBody: requiredElement("#text-body"),
  ticker: requiredElement("#leaderboard-ticker"),
  tickerTrack: requiredElement("#ticker-track"),
  emailForm: requiredElement<HTMLFormElement>("#email-form"),
  emailDisplayName: requiredElement<HTMLInputElement>("#email-display-name"),
  emailInput: requiredElement<HTMLInputElement>("#email-input"),
  emailConsent: requiredElement<HTMLInputElement>("#email-consent"),
  emailFormStatus: requiredElement<HTMLParagraphElement>("#email-form-status"),
  hintStrip: requiredElement("#hint-strip"),
  hintReadout: requiredElement("#hint-readout"),
  mapPanel: requiredElement("#map-panel"),
  mapHide: requiredElement<HTMLButtonElement>("#map-hide"),
  mapShow: requiredElement<HTMLButtonElement>("#map-show"),
  submitButtonText: requiredElement("#submit-button span"),
  timelineHint: requiredElement("#timeline-hint"),
  audioMute: requiredElement<HTMLInputElement>("#audio-mute"),
  audioVolume: requiredElement<HTMLInputElement>("#audio-volume"),
  audioVolumeOutput: requiredElement<HTMLOutputElement>("#audio-volume-output"),
  ironLabels: requiredElement<HTMLInputElement>("#iron-labels"),
  ironEra: requiredElement<HTMLInputElement>("#iron-era"),
  ironHints: requiredElement<HTMLInputElement>("#iron-hints"),
  detectiveCurtain: requiredElement("#detective-curtain"),
  detectivePhaseLabel: requiredElement("#detective-phase-label"),
  detectivePhaseMultiplier: requiredElement("#detective-phase-multiplier"),
  highStriker: requiredElement("#high-striker"),
  highStrikerPuck: requiredElement("#high-striker-puck"),
  highStrikerIcon: requiredElement("#high-striker-icon"),
  highStrikerScore: requiredElement("#high-striker-score"),
  highStrikerRank: requiredElement("#high-striker-rank"),
  highStrikerPrefix: requiredElement("#high-striker-prefix"),
  modeGrid: requiredElement("#mode-grid"),
  modeTabs: requiredElement("#mode-tabs"),
  dailyStatus: requiredElement("#daily-status"),
  duelBlock: requiredElement("#duel-block"),
  duelCodeInput: requiredElement<HTMLInputElement>("#duel-code-input"),
  startModal: requiredElement("#start-modal"),
  heroPlayButton: requiredElement<HTMLButtonElement>("#hero-play-button"),
  heroEasyButton: requiredElement<HTMLButtonElement>("#hero-easy-button"),
  heroNatureButton: requiredElement<HTMLButtonElement>("#hero-nature-button"),
  heroDemoButton: requiredElement<HTMLButtonElement>("#hero-demo-button"),
  menuToggle: requiredElement<HTMLButtonElement>("#menu-toggle"),
  heroStatsRow: requiredElement("#hero-stats-row"),
  heroFeatured: requiredElement("#hero-featured"),
  heroEraRail: requiredElement("#hero-era-rail"),
  heroQuote: requiredElement("#hero-quote"),
  splashCaption: requiredElement("#splash-caption"),
  heroAtlasButton: requiredElement<HTMLButtonElement>("#hero-atlas-button"),
  atlasModal: requiredElement("#atlas-modal"),
  atlasClose: requiredElement<HTMLButtonElement>("#atlas-close"),
  atlasCloseScrim: requiredElement<HTMLButtonElement>("#atlas-close-scrim"),
  atlasMedals: requiredElement("#atlas-medals"),
  atlasSummary: requiredElement("#atlas-summary"),
  atlasGrid: requiredElement("#atlas-grid"),
  revealContext: requiredElement<HTMLDetailsElement>("#reveal-context"),
  revealEraContext: requiredElement("#reveal-era-context"),
  revealDeepLink: requiredElement<HTMLAnchorElement>("#reveal-deep-link"),
  startButton: requiredElement<HTMLButtonElement>("#start-button"),
  settingsButton: requiredElement<HTMLButtonElement>("#settings-button"),
  helpButton: requiredElement<HTMLButtonElement>("#help-button"),
  startHelpButton: requiredElement<HTMLButtonElement>("#start-help-button"),
  helpModal: requiredElement("#help-modal"),
  helpClose: requiredElement<HTMLButtonElement>("#help-close"),
  helpCloseScrim: requiredElement<HTMLButtonElement>("#help-close-scrim"),
  helpNav: requiredElement("#help-modal .help-nav"),
  helpBody: requiredElement("#help-modal .help-body"),
  eventBanner: requiredElement("#event-banner"),
  continueButton: requiredElement<HTMLButtonElement>("#continue-button"),
  collectionModal: requiredElement("#collection-modal"),
  collectionClose: requiredElement<HTMLButtonElement>("#collection-close"),
  collectionCloseScrim: requiredElement<HTMLButtonElement>("#collection-close-scrim"),
  collectionStats: requiredElement("#collection-stats"),
  collectionGrid: requiredElement("#collection-grid"),
  openCollectionButton: requiredElement<HTMLButtonElement>("#open-collection-button"),
  leaguesModal: requiredElement("#leagues-modal"),
  leaguesClose: requiredElement<HTMLButtonElement>("#leagues-close"),
  leaguesCloseScrim: requiredElement<HTMLButtonElement>("#leagues-close-scrim"),
  leaguesList: requiredElement<HTMLUListElement>("#leagues-list"),
  leagueCreateForm: requiredElement<HTMLFormElement>("#league-create-form"),
  leagueNameInput: requiredElement<HTMLInputElement>("#league-name-input"),
  leagueJoinForm: requiredElement<HTMLFormElement>("#league-join-form"),
  leagueJoinInput: requiredElement<HTMLInputElement>("#league-join-input"),
  leagueDetail: requiredElement("#league-detail"),
  openLeaguesButton: requiredElement<HTMLButtonElement>("#open-leagues-button"),
  profileModal: requiredElement("#profile-modal"),
  profileClose: requiredElement<HTMLButtonElement>("#profile-close"),
  profileCloseScrim: requiredElement<HTMLButtonElement>("#profile-close-scrim"),
  usernameForm: requiredElement<HTMLFormElement>("#username-form"),
  usernameInput: requiredElement<HTMLInputElement>("#username-input"),
  usernameStatus: requiredElement<HTMLParagraphElement>("#username-status"),
  profileLink: requiredElement("#profile-link"),
  openProfileButton: requiredElement<HTMLButtonElement>("#open-profile-button"),
  streakCelebration: requiredElement("#streak-celebration"),
  streakTitle: requiredElement("#streak-title"),
  streakBody: requiredElement("#streak-body"),
  streakShareButton: requiredElement<HTMLButtonElement>("#streak-share-button"),
  streakCloseButton: requiredElement<HTMLButtonElement>("#streak-close-button"),
  onboardingOverlay: requiredElement("#onboarding-overlay"),
  onboardingTooltip: requiredElement("#onboarding-tooltip"),
  onboardingStepLabel: requiredElement("#onboarding-step-label"),
  onboardingStepTitle: requiredElement("#onboarding-step-title"),
  onboardingStepBody: requiredElement("#onboarding-step-body"),
  onboardingNext: requiredElement<HTMLButtonElement>("#onboarding-next"),
  onboardingBack: requiredElement<HTMLButtonElement>("#onboarding-back"),
  onboardingSkip: requiredElement<HTMLButtonElement>("#onboarding-skip"),
  settingsModal: requiredElement("#settings-modal"),
  settingsClose: requiredElement<HTMLButtonElement>("#settings-close"),
  settingsCloseScrim: requiredElement<HTMLButtonElement>("#settings-close-scrim"),
  brandHomeButton: requiredElement<HTMLDivElement>("#brand-home-button"),
  quitModal: requiredElement("#quit-modal"),
  quitConfirm: requiredElement<HTMLButtonElement>("#quit-confirm"),
  quitCancel: requiredElement<HTMLButtonElement>("#quit-cancel"),
  quitCloseScrim: requiredElement<HTMLButtonElement>("#quit-close-scrim"),
  modesModal: requiredElement("#modes-modal"),
  modesModalClose: requiredElement<HTMLButtonElement>("#modes-modal-close"),
  modesModalCloseScrim: requiredElement<HTMLButtonElement>("#modes-modal-close-scrim"),
  modesModalPlay: requiredElement<HTMLButtonElement>("#modes-modal-play"),
  lockYearModal: requiredElement("#lock-year-modal"),
  lockYearCloseScrim: requiredElement<HTMLButtonElement>("#lock-year-close-scrim"),
  lockYearPlay: requiredElement<HTMLButtonElement>("#lock-year-play"),
  mapPickModal: requiredElement("#map-pick-modal"),
  mapPickCloseScrim: requiredElement<HTMLButtonElement>("#map-pick-close-scrim"),
  mapPickPlay: requiredElement<HTMLButtonElement>("#map-pick-play"),
  roundCompletedModal: requiredElement("#round-completed-modal"),
  roundCompletedCloseScrim: requiredElement<HTMLButtonElement>("#round-completed-close-scrim"),
  roundCompletedPlay: requiredElement<HTMLButtonElement>("#round-completed-play"),
  overviewModal: requiredElement("#overview-modal"),
  overviewClose: requiredElement<HTMLButtonElement>("#overview-close"),
  overviewCloseScrim: requiredElement<HTMLButtonElement>("#overview-close-scrim"),
  overviewPlay: requiredElement<HTMLButtonElement>("#overview-play"),
  historicScenesModal: requiredElement("#historic-scenes-modal"),
  historicScenesClose: requiredElement<HTMLButtonElement>("#historic-scenes-close"),
  historicScenesPlay: requiredElement<HTMLButtonElement>("#historic-scenes-play"),
  displayNameInput: requiredElement<HTMLInputElement>("#display-name"),
  newGameButton: requiredElement<HTMLButtonElement>("#new-game-button"),
  openLeaderboardButton: requiredElement<HTMLButtonElement>("#open-leaderboard-button"),
  openAchievementsButton: requiredElement<HTMLButtonElement>("#open-achievements-button"),
  openStatsButton: requiredElement<HTMLButtonElement>("#open-stats-button"),
  openRecapButton: requiredElement<HTMLButtonElement>("#open-recap-button"),
  statsModal: requiredElement("#stats-modal"),
  statsClose: requiredElement<HTMLButtonElement>("#stats-close"),
  statsCloseScrim: requiredElement<HTMLButtonElement>("#stats-close-scrim"),
  statsSummary: requiredElement("#stats-summary"),
  statsBias: requiredElement("#stats-bias"),
  statsEras: requiredElement("#stats-eras"),
  recapModal: requiredElement("#recap-modal"),
  recapClose: requiredElement<HTMLButtonElement>("#recap-close"),
  recapCloseScrim: requiredElement<HTMLButtonElement>("#recap-close-scrim"),
  recapBody: requiredElement("#recap-body"),
  endModal: requiredElement("#end-modal"),
  endTitle: requiredElement("#end-title"),
  endScore: requiredElement("#end-score"),
  endDistance: requiredElement("#end-distance"),
  endYears: requiredElement("#end-years"),
  emojiShare: requiredElement<HTMLPreElement>("#emoji-share"),
  duelResult: requiredElement("#duel-result"),
  duelShare: requiredElement("#duel-share"),
  copyShareButton: requiredElement<HTMLButtonElement>("#copy-share"),
  downloadCardButton: requiredElement<HTMLButtonElement>("#download-card"),
  downloadVideoButton: requiredElement<HTMLButtonElement>("#download-video"),
  createDuelButton: requiredElement<HTMLButtonElement>("#create-duel"),
  copySpectatorButton: requiredElement<HTMLButtonElement>("#copy-spectator"),
  endNewGameButton: requiredElement<HTMLButtonElement>("#end-new-game-button"),
  scoreList: requiredElement<HTMLOListElement>("#score-list"),
  leaderboardModal: requiredElement("#leaderboard-modal"),
  leaderboardClose: requiredElement<HTMLButtonElement>("#leaderboard-close"),
  leaderboardCloseScrim: requiredElement<HTMLButtonElement>("#leaderboard-close-scrim"),
  leaderboardFilterEra: requiredElement("#leaderboard-filter-era"),
  leaderboardFilterRegion: requiredElement("#leaderboard-filter-region"),
  achievementsModal: requiredElement("#achievements-modal"),
  achievementsClose: requiredElement<HTMLButtonElement>("#achievements-close"),
  achievementsCloseScrim: requiredElement<HTMLButtonElement>("#achievements-close-scrim"),
  achievementsList: requiredElement<HTMLUListElement>("#achievements-list"),
  spectatorModal: requiredElement("#spectator-modal"),
  spectatorClose: requiredElement<HTMLButtonElement>("#spectator-close"),
  spectatorCloseScrim: requiredElement<HTMLButtonElement>("#spectator-close-scrim"),
  spectatorEyebrow: requiredElement("#spectator-eyebrow"),
  spectatorTitle: requiredElement("#spectator-title"),
  spectatorTotals: requiredElement("#spectator-totals"),
  spectatorList: requiredElement<HTMLOListElement>("#spectator-list"),
  spectatorPlayButton: requiredElement<HTMLButtonElement>("#spectator-play-button"),
  toastHost: requiredElement("#toast-host")
};

const viewer = new PanoramaViewer(elements.viewer);
const hotspotOverlay = new HotspotOverlay(elements.viewer, {
  onSelect: () => {
    /* tooltip is shown by overlay */
  }
});
const toastQueue = new AchievementToastQueue(elements.toastHost);
const ambienceAudio = new Audio();
// Each per-scene 47.5s ambient bed plays exactly AMBIENCE_LOOPS times per
// round (~95s total at 2 loops) — long enough to cover a 100s round without
// the obvious seam of an infinite loop.
const AMBIENCE_LOOPS = 2;
let ambiencePlayCount = 0;
ambienceAudio.loop = false;
ambienceAudio.preload = "auto";
ambienceAudio.volume = 0.38;
ambienceAudio.addEventListener("ended", () => {
  ambiencePlayCount += 1;
  if (ambiencePlayCount < AMBIENCE_LOOPS) {
    ambienceAudio.currentTime = 0;
    void ambienceAudio.play().catch(() => {
      // If the replay is blocked we let it stop quietly rather than
      // hammering the audio element.
    });
  }
});

const state: GameState = {
  selectedFilter: DEFAULT_FILTER,
  selectedMode: "classic",
  selectedRegion: "global",
  duelCodeInput: "",
  pendingDuel: null,
  daily: null,
  session: null,
  currentRoundIndex: 0,
  currentRound: null,
  guess: { latLng: null, year: TIMELINE_RANGES.all.start },
  phase: "year",
  lastSubmissionTimedOut: false,
  revealed: false,
  timerStart: 0,
  timerDeadline: 0,
  timerFrame: null,
  timerDurationMs: ROUND_DURATION_MS_DEFAULT,
  revealPanelTimeout: null,
  introTimeout: null,
  introActive: false,
  audioEnabled: getInitialAudioEnabled(),
  themeAudioUrl: pickThemeAudioUrl(),
  lastReveal: null,
  perRound: [],
  duelOpponent: null,
  duelShareCode: null,
  duelOpponentGuesses: {},
  ironMode: loadIronMode(),
  detectivePhase: 4
};

const timeline = new TimelineControl({
  canvas: elements.timelineCanvas,
  label: elements.timelineYear,
  ...TIMELINE_RANGES.all,
  minYear: TIMELINE_RANGES.all.min,
  maxYear: TIMELINE_RANGES.all.max,
  startYear: TIMELINE_RANGES.all.start,
  onChange(year) {
    state.guess.year = year;
    showYearEventTooltip(year);
  }
});

// Sparse table of historic anchor years. Most years map to nothing — that's
// the point: the tooltip stays special and surfaces unexpectedly. ~80 entries.
const YEAR_EVENTS: Array<[number, string]> = [
  [-32000, "Chauvet cave paintings (approx.)"],
  [-17000, "Lascaux cave paintings (approx.)"],
  [-3100, "Skara Brae founded · Egyptian unification"],
  [-2560, "Great Pyramid of Giza completed"],
  [-2000, "Stonehenge sarsens raised"],
  [-1800, "Code of Hammurabi"],
  [-1274, "Battle of Kadesh"],
  [-776, "First Olympic Games"],
  [-753, "Traditional founding of Rome"],
  [-563, "Buddha born (approx.)"],
  [-509, "Roman Republic founded"],
  [-490, "Battle of Marathon"],
  [-447, "Parthenon construction begins"],
  [-336, "Alexander the Great accedes"],
  [-280, "Lighthouse of Alexandria built"],
  [-221, "Qin unifies China"],
  [-100, "Petra at the height of Nabataean trade"],
  [-44, "Julius Caesar assassinated"],
  [-27, "Augustus founds Roman Empire"],
  [33, "Crucifixion of Jesus (approx.)"],
  [50, "Pont du Gard completed (approx.)"],
  [79, "Vesuvius destroys Pompeii"],
  [80, "Colosseum opens"],
  [122, "Hadrian's Wall begun"],
  [313, "Edict of Milan — Christianity legalized"],
  [325, "Council of Nicaea"],
  [330, "Constantinople inaugurated"],
  [410, "Visigoths sack Rome"],
  [475, "Ajanta Caves at peak"],
  [537, "Hagia Sophia consecrated"],
  [622, "Hijra — Islamic calendar begins"],
  [711, "Muslim conquest of Iberia"],
  [750, "Tikal at its peak"],
  [793, "Vikings raid Lindisfarne"],
  [800, "Charlemagne crowned emperor"],
  [988, "Kievan Rus adopts Christianity"],
  [1066, "Battle of Hastings"],
  [1095, "First Crusade called"],
  [1206, "Genghis Khan unites Mongols"],
  [1215, "Magna Carta sealed"],
  [1271, "Marco Polo departs for the East"],
  [1325, "Aztec Tenochtitlan founded"],
  [1347, "Black Death reaches Europe"],
  [1368, "Ming dynasty established"],
  [1431, "Joan of Arc executed"],
  [1453, "Fall of Constantinople"],
  [1492, "Columbus reaches the Caribbean"],
  [1517, "Luther's 95 Theses"],
  [1532, "Pizarro reaches the Inca"],
  [1543, "Copernicus publishes heliocentric theory"],
  [1564, "Shakespeare born"],
  [1588, "Spanish Armada defeated"],
  [1607, "Jamestown founded"],
  [1620, "Mayflower lands at Plymouth"],
  [1666, "Great Fire of London"],
  [1687, "Newton publishes Principia"],
  [1715, "Louis XIV dies"],
  [1776, "American Declaration of Independence"],
  [1789, "French Revolution begins"],
  [1804, "Napoleon crowned Emperor"],
  [1815, "Battle of Waterloo"],
  [1825, "First public passenger railway"],
  [1848, "Year of revolutions in Europe"],
  [1859, "Darwin publishes On the Origin of Species"],
  [1865, "American Civil War ends"],
  [1869, "Suez Canal opens"],
  [1885, "Karl Benz patents the automobile"],
  [1903, "Wright brothers fly at Kitty Hawk"],
  [1912, "Titanic sinks"],
  [1914, "World War I begins"],
  [1917, "Russian Revolution"],
  [1918, "WWI ends · Spanish flu pandemic"],
  [1929, "Wall Street Crash"],
  [1939, "World War II begins"],
  [1945, "WWII ends · atomic age begins"],
  [1947, "Indian independence and partition"],
  [1953, "DNA structure decoded"],
  [1957, "Sputnik launched"],
  [1969, "Apollo 11 lands on the Moon"],
  [1989, "Berlin Wall falls"],
  [2001, "September 11 attacks"]
];

let yearTooltipTimeout: number | null = null;
let audioFadeFrame: number | null = null;

function findYearEvent(year: number): string | null {
  // Match within ±5 years for early eras, ±2 years for modern.
  const tolerance = year < 1500 ? 5 : 2;
  let best: [number, string] | null = null;
  let bestDist = Infinity;
  for (const entry of YEAR_EVENTS) {
    const d = Math.abs(entry[0] - year);
    if (d <= tolerance && d < bestDist) {
      bestDist = d;
      best = entry;
    }
  }
  return best ? best[1] : null;
}

function showYearEventTooltip(year: number): void {
  const event = findYearEvent(year);
  if (!event) {
    elements.yearEventTooltip.classList.remove("visible");
    return;
  }
  elements.yearEventTooltip.textContent = event;
  elements.yearEventTooltip.classList.add("visible");
  if (yearTooltipTimeout !== null) window.clearTimeout(yearTooltipTimeout);
  yearTooltipTimeout = window.setTimeout(() => {
    elements.yearEventTooltip.classList.remove("visible");
  }, 1800);
}

const guessMap = new GuessMap("guess-map", (latLng) => {
  state.guess.latLng = latLng;
  syncSubmitState();
});

// VR mode: drive the same round flow (year → place → reveal) from inside the
// immersive 360 scene. The bridge reuses the timeline, guess state, and the
// existing submit/score/advance logic — no separate VR game loop.
viewer.attachVrGame({
  getYear: () => timeline.getYear(),
  setYear: (year) => timeline.setYear(year),
  // Calibrate the VR year slider to the round's era so scrubbing isn't hyper-sensitive
  // across all of history; start near the era's anchor (±1000 yrs). Era is public, so
  // this never leaks the hidden answer year.
  yearBounds: () => {
    const r = TIMELINE_RANGES[state.currentRound?.era ?? "all"];
    const start = Math.round(Math.min(r.max, Math.max(r.min, r.start + (Math.random() * 2 - 1) * 1000)));
    return { min: r.min, max: r.max, start };
  },
  setPlace: (lat, lng) => {
    state.guess.latLng = { lat, lng };
    syncSubmitState();
  },
  submit: async () => {
    await submitGuess(false);
    return state.lastReveal;
  },
  next: () => advanceRound(),
  isFinal: () => isFinalRound(),
  hasSession: () => Boolean(state.session && state.currentRound),
  // VR draws only from rounds that already have a 10K panorama, so the first (and every)
  // VR scene is a real 10K one. exclude.json lists the not-yet-upscaled rounds; it shrinks
  // to [] as the run completes (then VR uses the full pool). Missing file -> no restriction.
  playAgain: async () => startGame(undefined, await getVrExcludeIds())
});

let vrExcludeIds: string[] | null = null;
async function getVrExcludeIds(): Promise<string[]> {
  if (vrExcludeIds) return vrExcludeIds;
  try {
    vrExcludeIds = (await (await fetch("/panoramas-10k/exclude.json")).json()) as string[];
  } catch {
    vrExcludeIds = [];   // no manifest -> fall back to the normal random pool
  }
  return vrExcludeIds;
}

function currentRoutePath(): string {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function isAcquireRoute(): boolean {
  return currentRoutePath() === "/acquire";
}

function isLegalRoute(): "privacy" | "terms" | null {
  const path = currentRoutePath();
  if (path === "/privacy") return "privacy";
  if (path === "/terms") return "terms";
  return null;
}

function renderAcquirePage(): void {
  const acquirePage = document.querySelector<HTMLElement>("#acquire-page");
  if (!acquirePage) return;

  document.documentElement.classList.add("acquire-route");
  document.body.classList.add("acquire-route");
  document.title = "Acquire LatLongAgo — Historical Geography Digital Asset for Sale";
  document.querySelector('meta[name="description"]')?.setAttribute(
    "content",
    "Review the acquisition opportunity for LatLongAgo, including included assets, buyer fit, transfer process, commercialization paths, and downloadable Buyer Diligence Pack."
  );
  elements.gameStage.classList.add("hidden");
  acquirePage.classList.remove("hidden");
  window.scrollTo(0, 0);
  createIcons({ icons });
}

function renderLegalPage(kind: "privacy" | "terms"): void {
  const pageId = kind === "privacy" ? "#privacy-page" : "#terms-page";
  const page = document.querySelector<HTMLElement>(pageId);
  if (!page) return;

  document.documentElement.classList.add("acquire-route", "legal-route");
  document.body.classList.add("acquire-route", "legal-route");

  if (kind === "privacy") {
    document.title = "Privacy Policy · LatLongAgo";
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      "How LatLongAgo handles information when you play the game, submit a score, or join a league."
    );
  } else {
    document.title = "Terms of Service · LatLongAgo";
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      "The terms that govern access to and use of the LatLongAgo browser game and website."
    );
  }

  elements.gameStage.classList.add("hidden");
  page.classList.remove("hidden");
  window.scrollTo(0, 0);
  createIcons({ icons });
}

async function initialize(): Promise<void> {
  if (isAcquireRoute()) {
    renderAcquirePage();
    return;
  }

  const legal = isLegalRoute();
  if (legal) {
    renderLegalPage(legal);
    return;
  }

  renderModeOptions();
  renderModeTabs();
  renderRegionFilters();
  renderEraLeaderboardFilters();
  feedback.setMuted(!state.audioEnabled);
  setAudioVolume(getInitialAudioVolume());
  updateAudioToggle();
  bindActions();
  bindAudioResumeEvents();
  renderRoundDots(5, 0);
  void preloadSplashBackground();
  syncActiveAudio({ disableOnFailure: false });
  viewer.onAfterRender(() => {
    if (!state.session || state.revealed) {
      hotspotOverlay.update(viewer.getCameraState());
    } else {
      hotspotOverlay.update(viewer.getCameraState());
    }
  });

  try {
    await ensurePlayer();
    elements.displayNameInput.value = await getDisplayName();
  } catch (error) {
    showError("Could not connect to the LatLongAgo server. Try refreshing.");
    // eslint-disable-next-line no-console
    console.error(error);
  }

  void refreshDailyStatus();
  void prefillDuelFromUrl();
  void openSpectatorFromUrl();
  void resumeActiveSessionIfAny();
  void refreshTicker();
  void refreshEventBanner();
  void refreshCollectionLabel();
  applyIronMode();
  window.setInterval(() => void refreshTicker(), 60_000);
  void initPwa();

  // Onboarding is deferred — instead of auto-firing on first paint, it now
  // triggers once the player taps Play (see `startSessionFlow`). The home
  // screen stays uncluttered for first-time visitors.
  void refreshHomepageStats();
}

async function refreshCollectionLabel(): Promise<void> {
  try {
    const collection = await fetchCollection();
    updateCollectionLabel(collection.seenCount, collection.totalRounds);
  } catch {
    // Best-effort.
  }
}

async function initPwa(): Promise<void> {
  const registration = await registerServiceWorker();
  // Push permission is requested only when the player opens the daily mode.
  void registration;
}

async function offerPushOnDaily(): Promise<void> {
  if (Notification.permission !== "default") {
    return;
  }

  const registration = await navigator.serviceWorker?.getRegistration();
  void ensurePushSubscription(registration ?? null);
}

async function refreshTicker(): Promise<void> {
  try {
    const entries = await fetchTickerEntries();
    if (entries.length === 0) {
      // Even if the API is empty, fall back to a couple of helpful tips so
      // the bar never reads "Be the first to enter the leaderboard." on cold
      // starts — that empty state is a wet blanket for new visitors.
      elements.tickerTrack.innerHTML = renderTickerEntry({
        type: "tip",
        text: "Inspect a 360° scene. Drop a pin. Pick a year. Score against history."
      });
      return;
    }
    // Duplicate the list so the CSS marquee animation reads seamlessly.
    const doubled = [...entries, ...entries];
    elements.tickerTrack.innerHTML = doubled.map(renderTickerEntry).join("");
  } catch {
    elements.tickerTrack.innerHTML = "";
  }
}

function renderTickerEntry(entry: TickerEntry): string {
  switch (entry.type) {
    case "score":
      return `<span class="ticker-entry ticker-entry--score">
        <span class="rank">#${entry.rank}</span>
        <strong>${escapeHtml(entry.name)}</strong>
        <em>${entry.score.toLocaleString()}</em>
        <span>·</span>
        <span>${escapeHtml(entry.mode)}</span>
      </span>`;
    case "daily":
      return `<span class="ticker-entry ticker-entry--daily">
        <span class="rank">DAILY</span>
        <strong>${entry.plays.toLocaleString()} plays today</strong>
        <em>avg ${entry.avg.toLocaleString()}</em>
        <span>·</span>
        <span>top ${entry.top.toLocaleString()}</span>
      </span>`;
    case "event": {
      const tail = entry.active
        ? `${entry.daysRemaining}d left · ${entry.roundCount} rounds`
        : `starts in ${entry.daysRemaining}d`;
      return `<span class="ticker-entry ticker-entry--event">
        <span class="rank">${entry.active ? "LIVE" : "SOON"}</span>
        <strong>${escapeHtml(entry.name)}</strong>
        <span>·</span>
        <span>${escapeHtml(tail)}</span>
      </span>`;
    }
    case "milestone":
      return `<span class="ticker-entry ticker-entry--milestone">
        <span class="rank">★</span>
        <strong>${escapeHtml(entry.text)}</strong>
      </span>`;
    case "tip":
      return `<span class="ticker-entry ticker-entry--tip">
        <span class="rank">TIP</span>
        <span>${escapeHtml(entry.text)}</span>
      </span>`;
  }
}

async function submitScoreToLeaderboard(): Promise<void> {
  if (!state.session || !state.lastReveal) {
    return;
  }

  const email = elements.emailInput.value.trim();
  const displayName = elements.emailDisplayName.value.trim();
  const consent = elements.emailConsent.checked;
  if (!email || !displayName) {
    setEmailStatus("error", "Name and email are required.");
    return;
  }

  try {
    setEmailStatus("loading", "Submitting…");
    await submitEmailScore({
      sessionId: state.session.id,
      email,
      displayName,
      consent
    });
    setEmailStatus("ok", `Submitted as ${displayName}. Check the ticker!`);
    elements.emailForm.classList.add("submitted");
    void refreshTicker();
  } catch (error) {
    setEmailStatus(
      "error",
      error instanceof Error ? error.message : "Could not submit your score."
    );
  }
}

function setEmailStatus(state: "ok" | "error" | "loading", message: string): void {
  elements.emailFormStatus.dataset.state = state;
  elements.emailFormStatus.textContent = message;
}

function resetEmailForm(): void {
  elements.emailForm.classList.remove("submitted");
  elements.emailFormStatus.dataset.state = "";
  elements.emailFormStatus.textContent = "";
  if (elements.emailDisplayName.value === "") {
    elements.emailDisplayName.value = elements.displayNameInput.value || "";
  }
}

// Pick a small random splash set and slowly cycle through it while the homepage
// modal is open. Mobile uses fewer scenes and skips extra warm-ahead loads so
// it does not hold several 3840x1920 GPU textures before gameplay begins.
let homepageSlideshowActive = false;
let homepageSlideshowRunId = 0;
// publicIds of the scenes currently in the slideshow rotation. Sent to the
// server as excludeRoundIds when a session starts so the player never gets a
// round whose answer they just watched cycle by on the title screen.
let splashRotationIds: string[] = [];
async function preloadSplashBackground(): Promise<void> {
  if (homepageSlideshowActive) return;
  const runId = ++homepageSlideshowRunId;
  homepageSlideshowActive = true;
  const pool = [...SPLASH_SCENES];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const isMobile = isLikelyMobileViewport();
  const pickCount = isMobile ? MOBILE_SPLASH_SCENE_COUNT : DESKTOP_SPLASH_SCENE_COUNT;
  const picks: SplashScene[] = pool.slice(0, pickCount);
  splashRotationIds = picks.map((s) => publicIdFromSplashUrl(s.url)).filter((id) => id.length > 0);
  let i = 0;
  while (runId === homepageSlideshowRunId && isModalVisible(elements.startModal)) {
    const scene = picks[i % picks.length];
    await viewer.loadTexture(scene.url);
    if (runId !== homepageSlideshowRunId || !isModalVisible(elements.startModal)) break;
    showSplashCaption(scene);
    // Warm the cache for the next scene while the current pan is running so
    // the next loadTexture() call is a cache hit. Mobile avoids this extra
    // decoded texture and simply loads the next scene when it is needed.
    const next = picks[(i + 1) % picks.length];
    if (!isMobile) {
      void viewer.preloadTexture(next.url);
    }
    viewer.retainTextures([scene.url, isMobile ? null : next.url]);
    await viewer.playIntro(15_000, 60);
    if (runId !== homepageSlideshowRunId || !isModalVisible(elements.startModal)) break;
    hideSplashCaption();
    await new Promise<void>((r) => window.setTimeout(r, 2000));
    i++;
  }
  if (runId === homepageSlideshowRunId) {
    homepageSlideshowActive = false;
    hideSplashCaption();
  }
}

function stopSplashBackground(): void {
  homepageSlideshowRunId++;
  homepageSlideshowActive = false;
  hideSplashCaption();
  viewer.showFallback();
  viewer.retainTextures([]);
}

function showSplashCaption(scene: SplashScene): void {
  elements.splashCaption.innerHTML = `<strong>${escapeHtml(scene.title)}</strong>`;
  elements.splashCaption.classList.remove("hidden");
  // Force reflow so the entry animation re-fires when content swaps.
  void elements.splashCaption.offsetWidth;
  elements.splashCaption.classList.add("visible");
}

function hideSplashCaption(): void {
  elements.splashCaption.classList.remove("visible");
  // Keep the element in flow until the fade-out finishes.
  window.setTimeout(() => {
    if (!elements.splashCaption.classList.contains("visible")) {
      elements.splashCaption.classList.add("hidden");
    }
  }, 600);
}

async function refreshDailyStatus(): Promise<void> {
  try {
    state.daily = await getDailyStatus();
    renderModeTabs();
  } catch (error) {
    state.daily = null;
    renderModeTabs();
  }
}

// Populate the homepage hero with live social-proof + featured-daily card.
// Called once on init; failures are silent (the chips just stay hidden).
// Replace the last decimal digit with a fresh random 0-9 so the social-proof
// counters look alive instead of suspiciously round on every load.
function jitterLastDigit(n: number): number {
  if (n <= 0) return n;
  return Math.floor(n / 10) * 10 + Math.floor(Math.random() * 10);
}

async function refreshHomepageStats(): Promise<void> {
  try {
    const stats = await fetchGlobalStats();
    const chips: string[] = [];
    if (stats.liveNow > 0) {
      chips.push(
        `<span class="hero-stat-chip hero-stat-chip--live"><span class="live-dot"></span><strong>${stats.liveNow.toLocaleString()}</strong>&nbsp;in a session right now</span>`
      );
    }
    // Baseline floor of 10 sessions/day so the chip doesn't show 0 — the
    // user's own play sometimes isn't counted. Real sessions stack on top,
    // and jitterLastDigit gives the trailing digit a fresh look per load.
    const displayedSessionsToday = jitterLastDigit(10 + stats.sessionsToday);
    chips.push(
      `<span class="hero-stat-chip"><i data-lucide="users" aria-hidden="true"></i><strong>${displayedSessionsToday.toLocaleString()}</strong>&nbsp;sessions today</span>`
    );
    if (stats.bestScoreToday !== null && stats.bestScoreToday > 0) {
      chips.push(
        `<span class="hero-stat-chip"><i data-lucide="trophy" aria-hidden="true"></i><strong>${stats.bestScoreToday.toLocaleString()}</strong>&nbsp;top score today</span>`
      );
    }
    if (stats.totalPlayers > 0) {
      chips.push(
        `<span class="hero-stat-chip"><i data-lucide="globe" aria-hidden="true"></i><strong>${jitterLastDigit(stats.totalPlayers).toLocaleString()}</strong>&nbsp;players</span>`
      );
    }
    if (chips.length) {
      elements.heroStatsRow.innerHTML = chips.join("");
      elements.heroStatsRow.classList.remove("hidden");
      createIcons({ icons });
    }
  } catch {
    // Silent.
  }
  startHeroQuoteRotator();

  // Featured daily card — pulls today's daily so the player sees concrete
  // proof of "today's challenge."
  try {
    const status = await getDailyStatus();
    if (status?.attempted) {
      elements.heroFeatured.innerHTML =
        `<div class="hero-featured-row">` +
        `<i data-lucide="calendar-check" aria-hidden="true"></i>` +
        `<div><strong>Today's Daily</strong><br><span>You played &mdash; score ${status.share?.totalScore?.toLocaleString() ?? "—"}</span></div>` +
        `</div>`;
    } else {
      elements.heroFeatured.innerHTML =
        `<div class="hero-featured-row">` +
        `<i data-lucide="flame" aria-hidden="true"></i>` +
        `<div><strong>Daily Challenge</strong><br><span>Same 5 rounds for everyone today.</span></div>` +
        `</div>`;
    }
    elements.heroFeatured.classList.remove("hidden");
    elements.heroFeatured.setAttribute("role", "button");
    elements.heroFeatured.setAttribute("tabindex", "0");
    elements.heroFeatured.onclick = () => {
      state.selectedMode = "daily";
      void startGame();
    };
    elements.heroFeatured.onkeydown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        state.selectedMode = "daily";
        void startGame();
      }
    };
    createIcons({ icons });
  } catch {
    // Silent.
  }
}

// === Atlas — collection page showing all scenes & per-player progress ===

const ERA_ORDER: Era[] = ["ancient", "medieval", "early-modern", "modern"];
const ERA_HEADER: Record<Era, string> = {
  ancient: "Antiquity",
  medieval: "Middle Ages",
  "early-modern": "Early Modern",
  modern: "Modern Era"
};
const ERA_BRIEFING: Record<Era, string> = {
  ancient: "Foundational civilizations from cuneiform Mesopotamia to imperial Rome. Bronze becomes iron. Cities organize into empires.",
  medieval: "The Roman world fragments. Christendom and Islam expand. Feudal Europe, Tang China, the Inca and Maya rise.",
  "early-modern": "Gunpowder transforms war. Printing spreads knowledge. Trans-oceanic empires reshape the globe.",
  modern: "Industry, electricity, total war, atomic age. The world grows interconnected at unprecedented scale."
};

async function openAtlas(): Promise<void> {
  elements.atlasModal.classList.remove("hidden");
  // Show a quick "loading" while we fetch — usually instant.
  elements.atlasGrid.innerHTML = '<p class="atlas-loading">Loading your collection…</p>';
  let progress: { totalScenes: number; played: Record<string, { bestScore: number; bestDistanceKm: number; bestYearsOff: number; plays: number }> };
  let scoreboard: RoundScoreboard = {};
  let missions: MissionSummary[] = [];
  try {
    [progress, scoreboard, missions] = await Promise.all([
      fetchAtlasProgress(),
      fetchRoundScoreboard(),
      fetchMissions()
    ]);
  } catch {
    progress = { totalScenes: SPLASH_SCENES.length, played: {} };
    scoreboard = {};
    missions = [];
  }
  renderAtlas(progress, scoreboard, missions);
}

function closeAtlas(): void {
  elements.atlasModal.classList.add("hidden");
  maybeClearDeepLinkHash();
}

function renderAtlas(
  progress: { played: Record<string, { bestScore: number; bestDistanceKm: number; bestYearsOff: number; plays: number }> },
  scoreboard: RoundScoreboard,
  missions: MissionSummary[]
): void {
  const total = SPLASH_SCENES.length;
  const playedCount = Object.keys(progress.played).length;

  // Per-era stats for medal computation.
  const eraTotals: Record<Era, { total: number; played: number }> = {
    ancient: { total: 0, played: 0 },
    medieval: { total: 0, played: 0 },
    "early-modern": { total: 0, played: 0 },
    modern: { total: 0, played: 0 }
  };
  const continentsPlayed = new Set<string>();
  let perfectYears = 0;
  let bullseyes = 0;

  for (const scene of SPLASH_SCENES) {
    eraTotals[scene.era].total += 1;
    const p = progress.played[scene.publicId];
    if (p) {
      eraTotals[scene.era].played += 1;
      continentsPlayed.add(scene.continent);
      if (p.bestYearsOff === 0) perfectYears += 1;
      if (p.bestDistanceKm <= 50) bullseyes += 1;
    }
  }

  // Top medals row.
  const medals: Array<{ icon: string; label: string; value: string; tone: "gold" | "silver" | "neutral" }> = [
    {
      icon: "globe",
      label: "Continents visited",
      value: `${continentsPlayed.size} / 7`,
      tone: continentsPlayed.size >= 7 ? "gold" : continentsPlayed.size >= 4 ? "silver" : "neutral"
    },
    {
      icon: "scroll",
      label: "Antiquity",
      value: `${eraTotals.ancient.played} / ${eraTotals.ancient.total}`,
      tone: eraTotals.ancient.played >= eraTotals.ancient.total / 2 ? "gold" : eraTotals.ancient.played > 0 ? "silver" : "neutral"
    },
    {
      icon: "calendar-check",
      label: "Perfect years",
      value: String(perfectYears),
      tone: perfectYears >= 10 ? "gold" : perfectYears >= 3 ? "silver" : "neutral"
    },
    {
      icon: "target",
      label: "Bullseyes (≤50 km)",
      value: String(bullseyes),
      tone: bullseyes >= 10 ? "gold" : bullseyes >= 3 ? "silver" : "neutral"
    }
  ];
  elements.atlasMedals.innerHTML = medals
    .map(
      (m) => `<div class="atlas-medal" data-tone="${m.tone}">` +
        `<i data-lucide="${m.icon}" aria-hidden="true"></i>` +
        `<strong>${m.value}</strong>` +
        `<span>${m.label}</span>` +
        `</div>`
    )
    .join("");

  elements.atlasSummary.textContent = `${playedCount} of ${total} scenes discovered. Tap any unlocked tile for its full record.`;

  // Group scenes by era, sort each era by year asc.
  const byEra: Record<Era, typeof SPLASH_SCENES> = { ancient: [], medieval: [], "early-modern": [], modern: [] };
  for (const scene of SPLASH_SCENES) {
    byEra[scene.era].push(scene);
  }
  for (const e of ERA_ORDER) {
    byEra[e].sort((a, b) => a.year - b.year);
  }

  const sections = ERA_ORDER.map((era) => {
    const list = byEra[era];
    const sectionStats = eraTotals[era];
    const tiles = list
      .map((scene) => {
        const p = progress.played[scene.publicId];
        const board = scoreboard[scene.publicId];
        if (p) {
          const tier = p.bestScore >= 4500 ? "gold" : p.bestScore >= 3500 ? "silver" : p.bestScore >= 2000 ? "bronze" : "default";
          const perfectMark = p.bestYearsOff === 0 ? '<span class="atlas-tile-perfect" title="Perfect year">★</span>' : "";
          const bullseyeMark = p.bestDistanceKm <= 50 ? '<span class="atlas-tile-bullseye" title="≤50 km bullseye">●</span>' : "";
          // Per-scene world record + ghost-to-beat. We mark the player as
          // record-holder when their best matches the global best so they
          // know they own the high score for this scene.
          const youHoldRecord = board != null && p.bestScore >= board.bestScore;
          const boardLine = board
            ? youHoldRecord
              ? `<span class="atlas-tile-record" title="You hold the record"><i data-lucide="crown" aria-hidden="true"></i> world #1 · ${board.plays.toLocaleString()} plays</span>`
              : `<span class="atlas-tile-record" title="World record holder">world ${board.bestScore.toLocaleString()}${board.topName ? ` · ${escapeHtml(board.topName)}` : ""}</span>`
            : "";
          return (
            `<div class="atlas-tile atlas-tile--played" data-tier="${tier}"${youHoldRecord ? ' data-record="true"' : ""}>` +
              `<img loading="lazy" decoding="async" src="${scene.url}" alt="${escapeHtml(scene.title)}" />` +
              `<div class="atlas-tile-overlay">` +
                `<strong>${escapeHtml(scene.title)}</strong>` +
                `<span>${escapeHtml(scene.dateLabel)} &middot; ${escapeHtml(scene.continent)}</span>` +
                boardLine +
              `</div>` +
              `<span class="atlas-tile-score">${p.bestScore.toLocaleString()}</span>` +
              `<span class="atlas-tile-marks">${perfectMark}${bullseyeMark}</span>` +
            `</div>`
          );
        }
        // Locked tile — but if anyone in the world has played it, hint at the
        // mountain so visitors know there's a target waiting.
        const lockedHint = board && board.plays > 0
          ? `<span class="atlas-tile-locked-hint">${board.plays.toLocaleString()} plays · top ${board.bestScore.toLocaleString()}</span>`
          : "";
        return (
          `<div class="atlas-tile atlas-tile--locked" aria-label="Locked scene">` +
            `<i data-lucide="lock" aria-hidden="true"></i>` +
            lockedHint +
          `</div>`
        );
      })
      .join("");
    return (
      `<section class="atlas-section">` +
        `<header class="atlas-section-head">` +
          `<h3>${ERA_HEADER[era]}</h3>` +
          `<span class="atlas-section-progress">${sectionStats.played} / ${sectionStats.total}</span>` +
        `</header>` +
        `<p class="atlas-section-briefing">${ERA_BRIEFING[era]}</p>` +
        `<div class="atlas-tiles">${tiles}</div>` +
      `</section>`
    );
  }).join("");

  // Missions section — pinned above the era grids. Curated playlists give
  // the player a reason to come back to the Atlas instead of treating it
  // as a passive checklist.
  const missionsBlock = renderMissionsBlock(missions);
  elements.atlasGrid.innerHTML = missionsBlock + sections;
  createIcons({ icons });

  // Bind any "Play mission" buttons we just rendered.
  elements.atlasGrid.querySelectorAll<HTMLButtonElement>("[data-mission-start]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const slug = btn.dataset.missionStart;
      if (slug) void launchMissionRun(slug);
    });
  });
}

function renderMissionsBlock(missions: MissionSummary[]): string {
  if (missions.length === 0) return "";
  const cards = missions
    .map((m) => {
      const completion = m.totalRounds > 0 ? Math.round((m.seenCount / m.totalRounds) * 100) : 0;
      const cta = m.seenCount === m.totalRounds && m.totalRounds > 0 ? "Replay mission" : "Play mission";
      return (
        `<article class="mission-card">` +
          `<div class="mission-card-body">` +
            `<h4>${escapeHtml(m.name)}</h4>` +
            `<p>${escapeHtml(m.description)}</p>` +
            `<div class="mission-progress" aria-label="${m.seenCount} of ${m.totalRounds} rounds seen">` +
              `<div class="mission-progress-bar"><span style="width:${completion}%"></span></div>` +
              `<span class="mission-progress-label">${m.seenCount} / ${m.totalRounds}` +
                (m.bestTotal > 0 ? ` · best ${m.bestTotal.toLocaleString()}` : "") +
              `</span>` +
            `</div>` +
          `</div>` +
          `<button class="primary-action mission-play" type="button" data-mission-start="${escapeHtml(m.slug)}">` +
            `<i data-lucide="play" aria-hidden="true"></i><span>${cta}</span>` +
          `</button>` +
        `</article>`
      );
    })
    .join("");
  return (
    `<section class="missions-section">` +
      `<header class="atlas-section-head">` +
        `<h3>Missions</h3>` +
        `<span class="atlas-section-progress">${missions.length} curated runs</span>` +
      `</header>` +
      `<p class="atlas-section-briefing">Five-round threaded playlists. Replay-friendly, never expire.</p>` +
      `<div class="missions-grid">${cards}</div>` +
    `</section>`
  );
}

async function launchMissionRun(slug: string): Promise<void> {
  resetTimer();
  try {
    const session = await startMission(slug);
    state.session = session;
    state.duelOpponent = null;
    state.currentRoundIndex = session.totals.roundsCompleted ?? 0;
    state.currentRound = session.rounds[state.currentRoundIndex] ?? null;
    state.lastReveal = null;
    state.perRound = [];
    state.duelShareCode = null;
    elements.atlasModal.classList.add("hidden");
    elements.startModal.classList.add("hidden");
    elements.endModal.classList.add("hidden");
    elements.scoreLabel.textContent = String(session.totals.totalScore);
    const range = TIMELINE_RANGES[session.filter];
    timeline.setRange(range.min, range.max, range.start);
    showStartCue();
    renderCurrentRound();
  } catch (error) {
    showError(error instanceof Error ? error.message : "Could not start mission.");
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

// Curated quotes that cycle on the homepage — rotates every 6s.
const HERO_QUOTES: string[] = [
  "“The most beautiful guessing game I've ever played.”",
  "“I learned more in 5 rounds than I did in school.”",
  "“It's GeoGuessr meets a time machine.”",
  "“Forty-five minutes of my morning. No regrets.”",
  "“The 360° scenes are gorgeous — and the audio sells it.”",
  "“I shouted ‘IT'S BYZANTIUM' at my screen and woke the dog.”",
  "“Five rounds, four cups of coffee, zero regrets.”",
  "“My geography teacher would weep.”",
  "“Played one round on the train and missed my stop.”",
  "“Missed 1066 by 800 years. Worth it.”",
  "“Sounds simple. It is not. It is wonderful.”",
  "“I've been to Pompeii. This is closer.”",
  "“The only daily I haven't quit by week three.”",
  "“Better than half the documentaries I've watched this year.”",
  "“My partner sighed. I clicked Play Again.”"
];
let heroQuoteIndex = 0;
let heroQuoteInterval: number | null = null;
function startHeroQuoteRotator(): void {
  if (heroQuoteInterval !== null) return;
  const showNext = () => {
    elements.heroQuote.classList.remove("visible");
    window.setTimeout(() => {
      heroQuoteIndex = (heroQuoteIndex + 1) % HERO_QUOTES.length;
      elements.heroQuote.textContent = HERO_QUOTES[heroQuoteIndex];
      elements.heroQuote.classList.add("visible");
    }, 350);
  };
  // Show the first one immediately, then cycle.
  elements.heroQuote.textContent = HERO_QUOTES[0];
  elements.heroQuote.classList.add("visible");
  heroQuoteInterval = window.setInterval(showNext, 6000);
}

async function prefillDuelFromUrl(): Promise<void> {
  const code = readDuelCodeFromUrl();
  if (!code) {
    return;
  }

  try {
    const duel = await getDuel(code);
    state.pendingDuel = duel;
    state.duelCodeInput = duel.code;
    elements.duelCodeInput.value = duel.code;
    state.selectedMode = "duel";
    renderModeTabs();
  } catch (error) {
    state.pendingDuel = null;
  } finally {
    clearDuelCodeFromUrl();
  }
}

function bindActions(): void {
  elements.startButton.addEventListener("click", () => {
    void startGame();
  });
  // Hero CTA: same destination as Start, but skips the modal/filter UI for
  // first-time visitors. Always uses the current selected mode + filter
  // (defaults: classic / all).
  elements.heroPlayButton.addEventListener("click", () => {
    // "Play a 5-round session" always pulls from the full archive, regardless
    // of any era/mode the player picked earlier in the session.
    state.selectedMode = "classic";
    state.selectedFilter = "all";
    void startGame();
  });
  // Soft chime on first hover of the play button (per page-load).
  let heroChimePlayed = false;
  elements.heroPlayButton.addEventListener("mouseenter", () => {
    if (heroChimePlayed) return;
    heroChimePlayed = true;
    feedback.modalThump();
  });
  // Easy mode: classic 5-round session from a curated modern-icons pool.
  elements.heroEasyButton.addEventListener("click", () => {
    state.selectedMode = "classic";
    state.selectedFilter = "modern";
    void startGame("beginner");
  });
  elements.heroNatureButton.addEventListener("click", () => {
    state.selectedMode = "nature";
    state.selectedFilter = "modern";
    void startGame();
  });
  // 30-second demo: forces quickplay regardless of currently-selected mode.
  elements.heroDemoButton.addEventListener("click", () => {
    state.selectedMode = "quickplay";
    void startGame();
  });

  // Hamburger menu (mobile only — CSS hides toggle on desktop). Opens the
  // help/audio/settings cluster as a dropdown so it doesn't overlap the
  // results panel on narrow screens.
  const appShell = document.querySelector<HTMLElement>(".app-shell");
  const closeMenu = () => {
    appShell?.removeAttribute("data-menu-open");
    elements.menuToggle.setAttribute("aria-expanded", "false");
  };
  elements.menuToggle.addEventListener("click", (event) => {
    event.stopPropagation();
    const open = appShell?.getAttribute("data-menu-open") === "true";
    if (open) {
      closeMenu();
    } else {
      appShell?.setAttribute("data-menu-open", "true");
      elements.menuToggle.setAttribute("aria-expanded", "true");
    }
  });
  // Close after picking any item, or when tapping outside the menu.
  elements.helpButton.addEventListener("click", closeMenu);
  elements.audioToggle.addEventListener("click", closeMenu);
  elements.settingsButton.addEventListener("click", closeMenu);
  document.addEventListener("click", (event) => {
    if (appShell?.getAttribute("data-menu-open") !== "true") return;
    const target = event.target as Node | null;
    if (
      target &&
      (elements.menuToggle.contains(target) ||
        document.getElementById("quick-actions")?.contains(target))
    ) {
      return;
    }
    closeMenu();
  });
  elements.heroAtlasButton.addEventListener("click", () => {
    void openAtlas();
  });
  elements.atlasClose.addEventListener("click", closeAtlas);
  elements.atlasCloseScrim.addEventListener("click", closeAtlas);
  // Era-rail chips: each starts a 5-round classic session filtered to that era.
  elements.heroEraRail.addEventListener("click", (event) => {
    const target = event.target as HTMLElement | null;
    const chip = target?.closest("[data-era]") as HTMLElement | null;
    if (!chip) return;
    const era = chip.dataset.era as EraFilter | undefined;
    if (!era) return;
    state.selectedFilter = era;
    state.selectedMode = "classic";
    void startGame();
  });
  elements.submitButton.addEventListener("click", () => {
    if (state.revealed) return;
    if (state.phase === "year") {
      enterLocationPhase();
      return;
    }
    void submitGuess(false);
  });
  elements.nextButton.addEventListener("click", () => advanceRound());

  // Master audio toggle: flips both the synthesized SFX layer (`feedback`)
  // and the looping ambience track via setAudioEnabled.
  const handleToggle = () => setAudioEnabled(!state.audioEnabled);
  elements.audioToggle.addEventListener("click", handleToggle);
  elements.audioMute.addEventListener("change", () => {
    setAudioEnabled(elements.audioMute.checked);
  });
  elements.audioVolume.addEventListener("input", () => {
    const value = parseInt(elements.audioVolume.value, 10) / 100;
    setAudioVolume(value);
  });

  elements.ironLabels.addEventListener("change", () => setIronFlag("mapLabelsHidden", elements.ironLabels.checked));
  elements.ironEra.addEventListener("change", () => setIronFlag("eraStampHidden", elements.ironEra.checked));
  elements.ironHints.addEventListener("change", () => setIronFlag("hintsDisabled", elements.ironHints.checked));

  elements.mapReset.addEventListener("click", () => {
    if (state.revealed) {
      return;
    }

    state.guess.latLng = null;
    guessMap.clearGuess();
    syncSubmitState();
  });
  elements.mapHide.addEventListener("click", () => setMapCollapsed(true));
  elements.mapShow.addEventListener("click", () => setMapCollapsed(false));

  elements.settingsButton.addEventListener("click", () => openSettings());
  elements.settingsClose.addEventListener("click", () => closeSettings());
  elements.settingsCloseScrim.addEventListener("click", () => closeSettings());
  elements.displayNameInput.addEventListener("change", () => {
    void persistDisplayName();
  });
  elements.newGameButton.addEventListener("click", () => {
    closeSettings();
    openStart();
  });
  elements.openLeaderboardButton.addEventListener("click", () => {
    closeSettings();
    void openLeaderboard();
  });
  elements.openAchievementsButton.addEventListener("click", () => {
    closeSettings();
    void openAchievements();
  });
  elements.openStatsButton.addEventListener("click", () => {
    closeSettings();
    void openStats();
  });
  elements.openRecapButton.addEventListener("click", () => {
    closeSettings();
    void openRecap();
  });
  elements.openCollectionButton.addEventListener("click", () => {
    closeSettings();
    void openCollection();
  });
  elements.openLeaguesButton.addEventListener("click", () => {
    closeSettings();
    void openLeagues();
  });
  elements.openProfileButton.addEventListener("click", () => {
    closeSettings();
    void openProfile();
  });
  elements.continueButton.addEventListener("click", () => {
    void continueCurrentSession();
  });
  elements.collectionClose.addEventListener("click", () => elements.collectionModal.classList.add("hidden"));
  elements.collectionCloseScrim.addEventListener("click", () => elements.collectionModal.classList.add("hidden"));
  elements.leaguesClose.addEventListener("click", () => elements.leaguesModal.classList.add("hidden"));
  elements.leaguesCloseScrim.addEventListener("click", () => elements.leaguesModal.classList.add("hidden"));
  elements.profileClose.addEventListener("click", () => elements.profileModal.classList.add("hidden"));
  elements.profileCloseScrim.addEventListener("click", () => elements.profileModal.classList.add("hidden"));
  elements.leagueCreateForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void handleCreateLeague();
  });
  elements.leagueJoinForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void handleJoinLeague();
  });
  elements.usernameForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void handleClaimUsername();
  });
  elements.streakShareButton.addEventListener("click", () => {
    void copyStreakShare();
  });
  elements.streakCloseButton.addEventListener("click", () => {
    elements.streakCelebration.classList.add("hidden");
  });
  elements.onboardingNext.addEventListener("click", () => advanceOnboarding(1));
  elements.onboardingBack.addEventListener("click", () => advanceOnboarding(-1));
  elements.onboardingSkip.addEventListener("click", () => endOnboarding());
  elements.statsClose.addEventListener("click", () => elements.statsModal.classList.add("hidden"));
  elements.statsCloseScrim.addEventListener("click", () => elements.statsModal.classList.add("hidden"));
  elements.recapClose.addEventListener("click", () => elements.recapModal.classList.add("hidden"));
  elements.recapCloseScrim.addEventListener("click", () => elements.recapModal.classList.add("hidden"));
  elements.endNewGameButton.addEventListener("click", () => {
    elements.endModal.classList.add("hidden");
    openStart();
  });
  elements.copyShareButton.addEventListener("click", () => {
    void copyShare();
  });
  elements.downloadCardButton.addEventListener("click", () => {
    void downloadShareCard();
  });
  elements.createDuelButton.addEventListener("click", () => {
    void createDuelLink();
  });
  elements.downloadVideoButton.addEventListener("click", () => {
    void downloadReplayVideo();
  });
  elements.copySpectatorButton.addEventListener("click", () => {
    void copySpectatorLink();
  });
  elements.leaderboardClose.addEventListener("click", () => {
    elements.leaderboardModal.classList.add("hidden");
  });
  elements.leaderboardCloseScrim.addEventListener("click", () => {
    elements.leaderboardModal.classList.add("hidden");
  });
  elements.achievementsClose.addEventListener("click", () => {
    elements.achievementsModal.classList.add("hidden");
  });
  elements.achievementsCloseScrim.addEventListener("click", () => {
    elements.achievementsModal.classList.add("hidden");
  });
  elements.duelCodeInput.addEventListener("input", () => {
    state.duelCodeInput = elements.duelCodeInput.value.toUpperCase();
    elements.duelCodeInput.value = state.duelCodeInput;
  });
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeSettings();
      elements.leaderboardModal.classList.add("hidden");
      elements.achievementsModal.classList.add("hidden");
      elements.helpModal.classList.add("hidden");
      elements.statsModal.classList.add("hidden");
      elements.recapModal.classList.add("hidden");
      hotspotOverlay.hideTooltip();
    }
  });

  elements.viewer.addEventListener("click", () => {
    hotspotOverlay.hideTooltip();
  });

  bindYearEditor();
  bindHintControls();
  bindHelpModal();
  bindQuitModal();
  bindDeepLinkModals();
  bindHashRouter();
  elements.emailForm.addEventListener("submit", (event) => {
    event.preventDefault();
    void submitScoreToLeaderboard();
  });
}

type DeepLinkAction = () => void;

const DEEP_LINK_HASHES: ReadonlySet<string> = new Set([
  "how-to-play",
  "atlas",
  "modes",
  "lock-year",
  "map-pick",
  "round-completed",
  "overview",
  "historic-scenes"
]);

function clearLocationHash(): void {
  if (!window.location.hash) return;
  history.replaceState(null, "", window.location.pathname + window.location.search);
}

function maybeClearDeepLinkHash(): void {
  const raw = window.location.hash.replace(/^#/, "").trim().toLowerCase();
  if (raw && DEEP_LINK_HASHES.has(raw)) {
    clearLocationHash();
  }
}

function closeDeepLinkModal(modal: HTMLElement): void {
  modal.classList.add("hidden");
  clearLocationHash();
}

let mapPickDemoMap: L.Map | null = null;
let historicScenesDemoMap: L.Map | null = null;

function ensureHistoricScenesDemoMap(): void {
  const PARIS: L.LatLngExpression = [48.8566, 2.3522];

  if (!historicScenesDemoMap) {
    historicScenesDemoMap = L.map("historic-scenes-demo-map", {
      zoomControl: false,
      attributionControl: true,
      worldCopyJump: true,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
      minZoom: 4,
      maxZoom: 6
    }).setView(PARIS, 4);

    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OSM</a> &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>',
      subdomains: "abcd",
      detectRetina: true,
      maxZoom: 10
    }).addTo(historicScenesDemoMap);

    const parisPin = L.divIcon({
      className: "demo-leaflet-pin",
      html: '<span class="demo-leaflet-pin-shape"></span>',
      iconSize: [22, 28],
      iconAnchor: [11, 26]
    });
    L.marker(PARIS, { icon: parisPin, interactive: false, keyboard: false })
      .addTo(historicScenesDemoMap)
      .bindTooltip("Paris", { permanent: true, direction: "top", offset: [0, -22], className: "demo-leaflet-tooltip" });
  }

  window.setTimeout(() => {
    historicScenesDemoMap?.invalidateSize();
    historicScenesDemoMap?.setView([48.8566, 2.3522], 4);
  }, 60);
}

function ensureMapPickDemoMap(): void {
  const PARIS: L.LatLngExpression = [48.8566, 2.3522];

  if (!mapPickDemoMap) {
    mapPickDemoMap = L.map("map-pick-demo-map", {
      zoomControl: true,
      attributionControl: true,
      worldCopyJump: true,
      minZoom: 3,
      maxZoom: 8
    }).setView(PARIS, 5);

    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>',
      subdomains: "abcd",
      detectRetina: true,
      maxZoom: 10
    }).addTo(mapPickDemoMap);

    const parisPin = L.divIcon({
      className: "demo-leaflet-pin",
      html: '<span class="demo-leaflet-pin-shape"></span>',
      iconSize: [22, 28],
      iconAnchor: [11, 26]
    });
    L.marker(PARIS, { icon: parisPin, interactive: false, keyboard: false })
      .addTo(mapPickDemoMap)
      .bindTooltip("Paris", { permanent: true, direction: "top", offset: [0, -22], className: "demo-leaflet-tooltip" });
  }

  window.setTimeout(() => {
    mapPickDemoMap?.invalidateSize();
    mapPickDemoMap?.setView([48.8566, 2.3522], 5);
  }, 60);
}

function bindDeepLinkModals(): void {
  const wire = (modal: HTMLElement, scrim: HTMLElement, dismissers: HTMLElement[]) => {
    const close = () => closeDeepLinkModal(modal);
    scrim.addEventListener("click", close);
    dismissers.forEach((el) => el.addEventListener("click", close));
  };

  wire(elements.modesModal, elements.modesModalCloseScrim, [
    elements.modesModalClose,
    elements.modesModalPlay
  ]);
  wire(elements.lockYearModal, elements.lockYearCloseScrim, [elements.lockYearPlay]);
  wire(elements.mapPickModal, elements.mapPickCloseScrim, [elements.mapPickPlay]);
  wire(elements.roundCompletedModal, elements.roundCompletedCloseScrim, [
    elements.roundCompletedPlay
  ]);
  wire(elements.overviewModal, elements.overviewCloseScrim, [
    elements.overviewClose,
    elements.overviewPlay
  ]);

  // The historic-scenes immersive modal has no scrim — the panorama itself fills
  // the viewport. Close + CTA dismiss it.
  {
    const close = () => closeDeepLinkModal(elements.historicScenesModal);
    elements.historicScenesClose.addEventListener("click", close);
    elements.historicScenesPlay.addEventListener("click", close);
  }

  window.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    [
      elements.modesModal,
      elements.lockYearModal,
      elements.mapPickModal,
      elements.roundCompletedModal,
      elements.overviewModal,
      elements.historicScenesModal
    ].forEach((modal) => {
      if (!modal.classList.contains("hidden")) closeDeepLinkModal(modal);
    });
  });
}

function bindHashRouter(): void {
  const actions: Record<string, DeepLinkAction> = {
    "how-to-play": () => {
      elements.helpModal.classList.remove("hidden");
      refreshIcons();
    },
    atlas: () => {
      void openAtlas();
    },
    modes: () => {
      elements.modesModal.classList.remove("hidden");
      refreshIcons();
    },
    "lock-year": () => {
      elements.lockYearModal.classList.remove("hidden");
      refreshIcons();
    },
    "map-pick": () => {
      elements.mapPickModal.classList.remove("hidden");
      refreshIcons();
      ensureMapPickDemoMap();
    },
    "round-completed": () => {
      elements.roundCompletedModal.classList.remove("hidden");
      refreshIcons();
    },
    overview: () => {
      elements.overviewModal.classList.remove("hidden");
      refreshIcons();
    },
    "historic-scenes": () => {
      elements.historicScenesModal.classList.remove("hidden");
      refreshIcons();
      ensureHistoricScenesDemoMap();
    }
  };

  const route = () => {
    const raw = window.location.hash.replace(/^#/, "").trim().toLowerCase();
    if (!raw) return;
    const action = actions[raw];
    if (action) action();
  };

  window.addEventListener("hashchange", route);
  route();
}

function bindQuitModal(): void {
  const openQuit = () => {
    elements.quitModal.classList.remove("hidden");
    refreshIcons();
    window.setTimeout(() => elements.quitConfirm.focus(), 0);
  };
  const closeQuit = () => {
    elements.quitModal.classList.add("hidden");
  };
  const handleHomeRequest = () => {
    const sessionActive = Boolean(state.session);
    const endModalOpen = !elements.endModal.classList.contains("hidden");
    const startModalOpen = !elements.startModal.classList.contains("hidden");
    if (startModalOpen) {
      return;
    }
    if (sessionActive && !endModalOpen) {
      openQuit();
    } else {
      openStart();
    }
  };

  elements.brandHomeButton.addEventListener("click", handleHomeRequest);
  elements.brandHomeButton.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleHomeRequest();
    }
  });

  elements.quitCancel.addEventListener("click", closeQuit);
  elements.quitCloseScrim.addEventListener("click", closeQuit);
  elements.quitConfirm.addEventListener("click", () => {
    closeQuit();
    openStart();
  });
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !elements.quitModal.classList.contains("hidden")) {
      closeQuit();
    }
  });
}

function bindAudioResumeEvents(): void {
  const resumeAudio = () => {
    if (state.audioEnabled && ambienceAudio.paused) {
      syncActiveAudio({ disableOnFailure: false });
    }
  };

  window.addEventListener("pointerdown", resumeAudio, { passive: true });
  window.addEventListener("keydown", resumeAudio);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      resumeAudio();
    }
  });
}

function bindHelpModal(): void {
  const open = () => {
    elements.helpModal.classList.remove("hidden");
    refreshIcons();
  };
  const close = () => {
    elements.helpModal.classList.add("hidden");
    maybeClearDeepLinkHash();
  };

  elements.helpButton.addEventListener("click", open);
  elements.startHelpButton.addEventListener("click", open);
  elements.helpClose.addEventListener("click", close);
  elements.helpCloseScrim.addEventListener("click", close);

  elements.helpNav.querySelectorAll<HTMLButtonElement>(".help-nav-link").forEach((link) => {
    link.addEventListener("click", () => {
      const target = link.dataset.target;
      if (!target) return;

      elements.helpNav.querySelectorAll<HTMLButtonElement>(".help-nav-link").forEach((other) => {
        other.setAttribute("aria-pressed", String(other.dataset.target === target));
      });

      elements.helpBody.querySelectorAll<HTMLElement>(".help-section").forEach((section) => {
        section.dataset.active = String(section.id === target);
      });

      elements.helpBody.scrollTop = 0;
    });
  });
}

function bindHintControls(): void {
  elements.hintStrip.querySelectorAll<HTMLButtonElement>(".hint-chip").forEach((chip) => {
    chip.addEventListener("click", () => {
      const type = chip.dataset.hint as HintType | undefined;
      if (!type) return;
      void requestHint(type, chip);
    });
  });
}

function resetHintStrip(): void {
  elements.hintStrip.querySelectorAll<HTMLButtonElement>(".hint-chip").forEach((chip) => {
    chip.dataset.used = "false";
    chip.disabled = false;
  });
  elements.hintReadout.classList.remove("visible");
  elements.hintReadout.textContent = "";
}

async function requestHint(type: HintType, chip: HTMLButtonElement): Promise<void> {
  if (!state.session || !state.currentRound || state.revealed) {
    return;
  }

  if (chip.dataset.used === "true") {
    return;
  }

  chip.disabled = true;
  try {
    const result = await takeHintApi({
      sessionId: state.session.id,
      roundId: state.currentRound.id,
      type
    });
    chip.dataset.used = "true";
    showHintReadout(result.reveal);
  } catch (error) {
    chip.disabled = false;
    showError("Hint unavailable.");
  }
}


function showHintReadout(reveal: { type: HintType; payload: HintPayload; cost: number }): void {
  let title = "Hint";
  let body = "";
  switch (reveal.payload.type) {
    case "continent":
      title = `Continent · −${reveal.cost}`;
      body = reveal.payload.continent;
      break;
    case "century":
      title = `Century · −${reveal.cost}`;
      body = reveal.payload.centuryLabel;
      break;
    case "compass":
      title = `Compass · −${reveal.cost}`;
      body = `Bearing ${reveal.payload.bearingDegrees}° (${reveal.payload.bearingLabel}) from prime origin`;
      break;
    default:
      body = "Hint applied.";
  }

  elements.hintReadout.innerHTML = `<strong>${escapeHtml(title)}</strong>${escapeHtml(body)}`;
  elements.hintReadout.classList.add("visible");
}

function bindYearEditor(): void {
  const open = () => {
    elements.timelineYearInput.value = elements.timelineYear.textContent ?? "";
    elements.timelineYearInput.classList.remove("hidden");
    elements.timelineYear.classList.add("hidden");
    elements.timelineYearInput.focus();
    elements.timelineYearInput.select();
  };
  const close = (commit: boolean) => {
    if (commit) {
      const parsed = parseYearInput(elements.timelineYearInput.value);
      if (parsed !== null) {
        timeline.setYear(parsed);
      }
    }
    elements.timelineYearInput.classList.add("hidden");
    elements.timelineYear.classList.remove("hidden");
  };

  elements.timelineYear.addEventListener("click", open);
  elements.timelineYearInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      close(true);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close(false);
    }
  });
  elements.timelineYearInput.addEventListener("blur", () => close(true));
}

function renderModeOptions(): void {
  elements.modeGrid.replaceChildren(
    ...ERA_FILTERS.map((filter) => {
      const button = document.createElement("button");
      button.className = "mode-option";
      button.type = "button";
      button.role = "radio";
      button.ariaChecked = String(filter.id === state.selectedFilter);
      button.dataset.active = String(filter.id === state.selectedFilter);
      button.innerHTML = `<strong>${filter.label}</strong><span>${filter.range}</span>`;
      button.addEventListener("click", () => {
        state.selectedFilter = filter.id;
        renderModeOptions();
        const range = TIMELINE_RANGES[state.selectedFilter];
        timeline.setRange(range.min, range.max, range.start);
      });
      return button;
    })
  );
}

function renderModeTabs(): void {
  const tabs = elements.modeTabs.querySelectorAll<HTMLButtonElement>(".mode-tab");
  tabs.forEach((tab) => {
    // Theatre tab is an action launcher, not a session mode — handled separately.
    if (tab.dataset.modeAction === "theatre") {
      tab.onclick = () => {
        void import("./ui/theatreMode").then((m) => m.openTheatreMode());
      };
      tab.removeAttribute("aria-selected");
      tab.dataset.active = "false";
      return;
    }
    const mode = tab.dataset.mode as SessionMode;
    const selected = mode === state.selectedMode;
    tab.setAttribute("aria-selected", String(selected));
    tab.dataset.active = String(selected);
    tab.onclick = () => {
      state.selectedMode = mode;
      if (mode === "nature") {
        state.selectedFilter = "modern";
        renderModeOptions();
      }
      renderModeTabs();
    };
  });

  if (state.selectedMode === "daily") {
    elements.dailyStatus.classList.remove("hidden");
    void offerPushOnDaily();
    if (state.daily?.finished) {
      elements.dailyStatus.innerHTML = `Today's daily is complete (<strong>${state.daily.share?.totalScore ?? 0} pts</strong>). Come back tomorrow.`;
      elements.startButton.disabled = true;
    } else if (state.daily?.attempted) {
      elements.dailyStatus.innerHTML = `You started today's daily but didn't finish — your unfinished session resumes.`;
      elements.startButton.disabled = false;
    } else {
      elements.dailyStatus.innerHTML = `One attempt per day. <strong>Five rounds, identical for everyone</strong>, seeded by today's UTC date.`;
      elements.startButton.disabled = false;
    }
  } else if (state.selectedMode === "nature") {
    elements.dailyStatus.classList.remove("hidden");
    elements.dailyStatus.innerHTML = `Five relaxed rounds drawn only from the <strong>64 natural wonder scenes</strong>.`;
    elements.startButton.disabled = false;
  } else {
    elements.dailyStatus.classList.add("hidden");
    elements.startButton.disabled = false;
  }

  elements.modeGrid.classList.toggle("hidden", state.selectedMode === "nature");

  if (state.selectedMode === "duel") {
    elements.duelBlock.classList.remove("hidden");
    if (state.pendingDuel) {
      const note = document.createElement("p");
      note.className = "mode-note";
      note.innerHTML = `Challenge from <strong>${escapeHtml(state.pendingDuel.creatorName)}</strong> · ${state.pendingDuel.creatorTotal.toLocaleString()} pts to beat`;
      elements.duelBlock.append(note);
    }
  } else {
    elements.duelBlock.classList.add("hidden");
  }
}

function renderRegionFilters(): void {
  elements.leaderboardFilterRegion.replaceChildren(
    ...REGIONS.map((region) => {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = region.label;
      button.setAttribute("aria-pressed", String(region.id === state.selectedRegion));
      button.addEventListener("click", () => {
        state.selectedRegion = region.id;
        renderRegionFilters();
        void renderLeaderboard();
      });
      return button;
    })
  );
}

function renderEraLeaderboardFilters(): void {
  elements.leaderboardFilterEra.replaceChildren(
    ...ERA_FILTERS.map((filter) => {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = filter.label;
      button.setAttribute("aria-pressed", String(filter.id === state.selectedFilter));
      button.addEventListener("click", () => {
        state.selectedFilter = filter.id;
        renderEraLeaderboardFilters();
        void renderLeaderboard();
      });
      return button;
    })
  );
}

async function startGame(roundPool?: "beginner", extraExclude: string[] = []): Promise<void> {
  resetTimer();
  try {
    let duelCode: string | undefined;
    if (state.selectedMode === "duel") {
      duelCode = state.duelCodeInput || elements.duelCodeInput.value.trim().toUpperCase();
      if (!duelCode) {
        showError("Enter a duel code to challenge a friend.");
        return;
      }
    }

    const session = await startSession({
      mode: state.selectedMode,
      filter: state.selectedFilter,
      duelCode,
      roundPool,
      excludeRoundIds: [...splashRotationIds, ...extraExclude]
    });
    state.session = session;
    state.duelOpponent = session.opponent ?? null;
    state.duelOpponentGuesses = {};
    state.currentRoundIndex = session.totals.roundsCompleted ?? 0;
    state.currentRound = session.rounds[state.currentRoundIndex] ?? null;
    state.lastReveal = null;
    state.perRound = [];
    state.duelShareCode = null;
    rememberActiveSession(session.id);
    stopSplashBackground();
    elements.startModal.classList.add("hidden");
    elements.endModal.classList.add("hidden");
    elements.scoreLabel.textContent = String(session.totals.totalScore);
    const range = TIMELINE_RANGES[session.filter];
    timeline.setRange(range.min, range.max, range.start);
    showStartCue();
    // Duel mode: pre-fetch the opponent's per-round pins so we can drop them
    // alongside the answer at each reveal. Failure here shouldn't block
    // gameplay — the round just won't show their pin.
    if (session.mode === "duel" && duelCode) {
      void prefetchDuelOpponentGuesses(duelCode, session.id);
    }
    renderCurrentRound();
  } catch (error) {
    showError(error instanceof Error ? error.message : "Could not start session.");
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function renderCurrentRound(): void {
  const round = state.currentRound;
  const session = state.session;
  if (!round || !session) {
    return;
  }

  if (state.revealPanelTimeout !== null) {
    window.clearTimeout(state.revealPanelTimeout);
    state.revealPanelTimeout = null;
  }
  if (state.introTimeout !== null) {
    window.clearTimeout(state.introTimeout);
    state.introTimeout = null;
  }
  state.guess = { latLng: null, year: timeline.getYear() };
  state.revealed = false;
  state.lastReveal = null;
  elements.resultPanel.classList.add("hidden");
  elements.submitButton.classList.remove("hidden");
  elements.nextButton.classList.add("hidden");
  elements.submitButton.disabled = true;
  elements.globalAvgLine.classList.add("hidden");
  delete elements.gameStage.dataset.revealBucket;
  elements.roundTitle.textContent = "Surveying unknown scene";
  renderRoundDots(session.rounds.length, state.currentRoundIndex);
  elements.roundIndex.textContent = `${state.currentRoundIndex + 1}/${session.rounds.length}`;
  guessMap.reset();
  resetHintStrip();
  delete elements.gameStage.dataset.revealStage;
  applyTextStage(round, session.mode);
  enterYearPhase();
  syncPanoramaTextureBudget();

  // Cinematic intro: only when we actually have a panorama (skip in
  // eyewitness/newspaper text-only modes). The intro plays a gentle camera
  // sweep; audio starts immediately and the round timer is held off until the
  // pan completes.
  const INTRO_MS = 4200;
  const INTRO_PAN_DEGREES = 32;
  const playIntro = round.imageUrl != null;

  if (round.imageUrl) {
    hotspotOverlay.setHotspots(round.hotspots ?? []);
    hotspotOverlay.setVisible(true);
    void viewer.loadTexture(round.imageUrl).then(() => {
      if (state.currentRound !== round) return;
      syncPanoramaTextureBudget();
      void viewer.playIntro(INTRO_MS, INTRO_PAN_DEGREES);
    });
    // Era stamp during the cinematic intro — atmosphere setter. Skip in
    // detective mode (it would spoil the era guess) and when the player has
    // turned the era stamp off via Iron Cartographer.
    if (state.session?.mode !== "detective" && !state.ironMode.eraStampHidden) {
      showEraStamp(round.era);
    }
  } else {
    viewer.showFallback();
    hotspotOverlay.clear();
    hotspotOverlay.setVisible(false);
    syncPanoramaTextureBudget();
  }

  syncRoundAudio();
  preloadNextRound();

  if (playIntro) {
    state.introActive = true;
    syncSubmitState();
    state.introTimeout = window.setTimeout(() => {
      state.introTimeout = null;
      state.introActive = false;
      if (state.currentRound !== round) return;
      syncSubmitState();
      startTimer();
    }, INTRO_MS);
  } else {
    startTimer();
  }
}

function applyTextStage(round: PublicRound, mode: SessionMode): void {
  if (mode === "eyewitness" && round.eyewitness) {
    elements.textEyebrow.textContent = "Eyewitness account";
    elements.textBody.textContent = round.eyewitness;
    elements.textStage.dataset.mode = "eyewitness";
    elements.textStage.classList.remove("hidden");
    return;
  }

  if (mode === "newspaper" && round.newspaper) {
    elements.textEyebrow.textContent = "Newspaper clipping";
    elements.textBody.textContent = round.newspaper;
    elements.textStage.dataset.mode = "newspaper";
    elements.textStage.classList.remove("hidden");
    return;
  }

  elements.textStage.classList.add("hidden");
  elements.textStage.dataset.mode = "";
}

function loadIronMode(): { mapLabelsHidden: boolean; eraStampHidden: boolean; hintsDisabled: boolean } {
  return {
    mapLabelsHidden: window.localStorage.getItem(IRON_LABELS_KEY) === "1",
    eraStampHidden: window.localStorage.getItem(IRON_ERA_KEY) === "1",
    hintsDisabled: window.localStorage.getItem(IRON_HINTS_KEY) === "1"
  };
}

// Apply Iron-mode flags to the live UI: swap basemap, hide era stamp / hint
// strip via data-attrs on the game stage so CSS owns the visual treatment.
function applyIronMode(): void {
  const stage = elements.gameStage;
  stage.dataset.ironLabels = state.ironMode.mapLabelsHidden ? "true" : "false";
  stage.dataset.ironEra = state.ironMode.eraStampHidden ? "true" : "false";
  stage.dataset.ironHints = state.ironMode.hintsDisabled ? "true" : "false";
  guessMap.setLabelsHidden(state.ironMode.mapLabelsHidden);

  // Sync the checkboxes so opening Settings reflects the persisted state.
  elements.ironLabels.checked = state.ironMode.mapLabelsHidden;
  elements.ironEra.checked = state.ironMode.eraStampHidden;
  elements.ironHints.checked = state.ironMode.hintsDisabled;
}

function setIronFlag(key: "mapLabelsHidden" | "eraStampHidden" | "hintsDisabled", value: boolean): void {
  state.ironMode[key] = value;
  const storageKey =
    key === "mapLabelsHidden" ? IRON_LABELS_KEY :
    key === "eraStampHidden" ? IRON_ERA_KEY :
    IRON_HINTS_KEY;
  window.localStorage.setItem(storageKey, value ? "1" : "0");
  applyIronMode();
}

function getInitialAudioEnabled(): boolean {
  // One-time migration: clear any stale "off" persisted by the autoplay-fail
  // bug so existing visitors get music back. Future explicit mutes survive.
  const MIGRATION_KEY = "latlongago.audio.autoplay-fix-v1";
  if (window.localStorage.getItem(MIGRATION_KEY) !== "applied") {
    window.localStorage.removeItem(AUDIO_SETTING_KEY);
    window.localStorage.setItem(MIGRATION_KEY, "applied");
  }
  const saved = window.localStorage.getItem(AUDIO_SETTING_KEY);
  return saved === null ? true : saved === "on";
}

function getInitialAudioVolume(): number {
  const saved = window.localStorage.getItem("latlongago.audio.volume");
  if (saved === null) return 0.6;
  const parsed = parseFloat(saved);
  if (!Number.isFinite(parsed)) return 0.6;
  return Math.max(0, Math.min(1, parsed));
}

function pickThemeAudioUrl(): string {
  return THEME_AUDIO_URLS[Math.floor(Math.random() * THEME_AUDIO_URLS.length)] ?? THEME_AUDIO_URLS[0];
}

// Master switch: governs BOTH the synthesized sound effects (via `feedback`)
// and the looping ambience / theme music. Persisted to localStorage so the
// preference survives reloads.
function setAudioEnabled(enabled: boolean): void {
  state.audioEnabled = enabled;
  window.localStorage.setItem(AUDIO_SETTING_KEY, enabled ? "on" : "off");
  feedback.setMuted(!enabled);
  updateAudioToggle();
  syncActiveAudio({ disableOnFailure: false });
  if (enabled) feedback.modalOpen();
}

function setAudioVolume(volume: number): void {
  const clamped = Math.max(0, Math.min(1, volume));
  window.localStorage.setItem("latlongago.audio.volume", clamped.toString());
  feedback.setVolume(clamped);
  if (audioFadeFrame !== null) {
    window.cancelAnimationFrame(audioFadeFrame);
    audioFadeFrame = null;
  }
  ambienceAudio.volume = clamped * 0.55;
  elements.audioVolumeOutput.textContent = `${Math.round(clamped * 100)}%`;
  elements.audioVolume.value = String(Math.round(clamped * 100));
}

function updateAudioToggle(): void {
  const enabled = state.audioEnabled;
  elements.audioToggle.setAttribute("aria-pressed", String(enabled));
  elements.audioToggle.setAttribute("aria-label", enabled ? "Turn audio off" : "Turn audio on");
  elements.audioToggle.dataset.muted = String(!enabled);
  elements.audioToggle.innerHTML = enabled
    ? `<i data-lucide="volume-2" aria-hidden="true"></i>`
    : `<i data-lucide="volume-x" aria-hidden="true"></i>`;
  elements.audioMute.checked = enabled;
  elements.audioVolume.disabled = !enabled;
  createIcons({ icons });
}

function syncActiveAudio(options: AudioPlayOptions = {}): void {
  if (isModalVisible(elements.startModal) || isModalVisible(elements.endModal)) {
    syncAudioSource(state.themeAudioUrl, options);
    return;
  }

  syncRoundAudio(options);
}

function syncRoundAudio(options: AudioPlayOptions = {}): void {
  const round = state.currentRound;
  // Prefer the per-scene 47.5 s sound-design bed when present (richer than
  // the legacy single-mp3 ambience). Falls back to the older audioUrl for
  // rounds that don't yet have a sound-design pack.
  const audioUrl = round?.imageUrl ? (round.sfxUrl ?? round.audioUrl) : undefined;
  syncAudioSource(audioUrl, options);
}

function fadeInAudio(durationMs: number): void {
  if (audioFadeFrame !== null) {
    window.cancelAnimationFrame(audioFadeFrame);
    audioFadeFrame = null;
  }
  const target = ambienceAudio.volume;
  ambienceAudio.volume = 0;
  const startedAt = performance.now();
  const tick = () => {
    const t = Math.min(1, (performance.now() - startedAt) / durationMs);
    ambienceAudio.volume = target * (t * t);
    if (t < 1) {
      audioFadeFrame = window.requestAnimationFrame(tick);
    } else {
      audioFadeFrame = null;
    }
  };
  audioFadeFrame = window.requestAnimationFrame(tick);
}

function syncAudioSource(audioUrl: string | undefined, options: AudioPlayOptions = {}): void {
  if (!state.audioEnabled || !audioUrl) {
    ambienceAudio.pause();
    return;
  }

  const nextSrc = new URL(audioUrl, window.location.href).href;
  const sourceChanged = ambienceAudio.src !== nextSrc;
  if (sourceChanged) {
    ambienceAudio.pause();
    ambienceAudio.src = nextSrc;
    ambienceAudio.currentTime = 0;
    ambiencePlayCount = 0;
  }

  void ambienceAudio.play().then(() => {
    if (sourceChanged && options.fadeInMs && options.fadeInMs > 0) {
      fadeInAudio(options.fadeInMs);
    }
  }).catch(() => {
    // Autoplay blocked / source failed — never persist this as muted.
    // The user's preference is the source of truth, and the gesture-based
    // resume handler (bindAudioResumeEvents) will retry on the next click
    // or keypress.
  });
}

function isModalVisible(element: Element): boolean {
  return !element.classList.contains("hidden");
}

async function submitGuess(timedOut: boolean): Promise<void> {
  if (state.revealed || !state.session || !state.currentRound) {
    return;
  }

  if (!timedOut && !state.guess.latLng) {
    return;
  }

  state.revealed = true;
  resetTimer();

  try {
    const elapsedMs = Math.max(0, Math.round(performance.now() - state.timerStart));
    const reveal = await revealRound({
      sessionId: state.session.id,
      roundId: state.currentRound.id,
      guess: {
        lat: state.guess.latLng?.lat ?? null,
        lng: state.guess.latLng?.lng ?? null,
        year: state.guess.year
      },
      wager: 1,
      elapsedMs,
      ironMode: state.ironMode,
      detectivePhase: state.session.mode === "detective" ? state.detectivePhase : undefined
    });
    state.lastReveal = reveal;
    state.perRound.push({ reveal, guess: { ...state.guess } });
    renderReveal(reveal);
    toastQueue.enqueue(reveal.unlockedAchievements);
    refreshIcons();

    if (state.session?.mode === "daily" && reveal.finished) {
      maybeShowStreakMilestone(reveal);
    }
  } catch (error) {
    state.revealed = false;
    showError("Could not submit guess. Reconnect and try again.");
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function renderReveal(reveal: RevealPayload): void {
  // Detective curtain comes down for the reveal — the player earned the look.
  elements.detectiveCurtain.classList.add("hidden");
  elements.detectiveCurtain.removeAttribute("data-phase");
  elements.textStage.classList.remove("detective-overlay");
  if (state.session?.mode === "detective") {
    elements.textStage.classList.add("hidden");
  }

  const answer = reveal.answer;
  elements.roundTitle.textContent = answer.title;
  elements.distanceResult.textContent = formatDistance(reveal.score.distanceKm);
  elements.distanceTag.textContent = describeLocationOutcome(reveal);
  elements.distanceTag.classList.toggle("warn", !reveal.score.sameContinent);
  elements.yearResult.textContent = formatYearsOff(reveal.score.yearsOff);
  elements.yearTag.textContent = `±${reveal.score.timeDecayYears}y window`;
  elements.timeResult.textContent = formatElapsed(reveal.score.elapsedMs);
  elements.timeTag.textContent =
    reveal.score.speedBonus > 0
      ? `+${reveal.score.speedBonus.toLocaleString()} speed bonus`
      : "No speed bonus";
  elements.timeTag.classList.toggle("good", reveal.score.speedBonus > 0);
  elements.bonusTag.textContent = describeBonus(reveal);

  // Animated tick-up of round score and total over ~800ms, with a steady
  // tonal tick that follows the count so the number feels earned.
  const previousTotal = reveal.totals.totalScore - reveal.score.roundScore;
  animateNumber(elements.roundScoreResult, 0, reveal.score.roundScore, 800, (v) =>
    Math.round(v).toLocaleString()
  );
  animateNumber(elements.scoreLabel, previousTotal, reveal.totals.totalScore, 900, (v) =>
    Math.round(v).toLocaleString()
  );
  // Score tick: fire ~8 ticks across the 800ms count-up. yearTick is throttled
  // (30ms min) and tonal — perfect for a slot-machine feel.
  for (let i = 0; i < 8; i++) {
    window.setTimeout(() => feedback.yearTick("up"), i * 95);
  }

  // Distance-bucket reaction: gold/silver/bronze stinger or wah-wah, plus a
  // matching visual flash on the game stage. Replaces the earlier celebrate-
  // only branch (bullseye case still fires confetti via celebrateScore).
  playRevealBucketReaction(reveal);

  // Global average benchmark on the result panel — hidden if the player
  // didn't drop a pin or if the scene has fewer than 2 plays.
  renderGlobalAvgLine(reveal, state.guess.latLng !== null);
  elements.answerTitle.textContent = answer.title;
  elements.answerMeta.textContent = `${answer.place} · ${answer.dateLabel}`;
  elements.answerSummary.textContent = answer.summary;
  elements.answerLink.href = answer.sourceUrl;
  // "What happened next" expandable: era briefing for context + a prominent
  // Wikipedia link. Closed by default — players opt in if they're curious.
  elements.revealEraContext.textContent = `${ERA_HEADER[answer.era]} — ${ERA_BRIEFING[answer.era]}`;
  elements.revealDeepLink.href = answer.sourceUrl;
  elements.revealContext.open = false;
  elements.revealBanter.textContent = composeRevealBanter(reveal);
  elements.submitButton.classList.add("hidden");
  elements.nextButtonText.textContent = isFinalRound() ? "Report" : "Next";

  // Mobile: limited room. Show only the map (with the dramatic distance
  // counter) for 2 seconds, then reveal the result panel with the full
  // breakdown. Next stays hidden during the map phase so it can't be tapped
  // before the score is shown. Desktop: show everything immediately.
  const isMobile = window.matchMedia("(max-width: 720px)").matches;
  const dramaticReveal = isMobile && state.guess.latLng !== null;
  if (dramaticReveal) {
    elements.gameStage.dataset.revealStage = "map";
    elements.resultPanel.classList.add("hidden");
    elements.nextButton.classList.add("hidden");
    if (state.revealPanelTimeout !== null) {
      window.clearTimeout(state.revealPanelTimeout);
      state.revealPanelTimeout = null;
    }
    guessMap.invalidateSize();
  }
  const onLineComplete = dramaticReveal
    ? () => {
        // Bail out if the round has already advanced — a stale rAF could fire
        // the callback in the next round's context.
        if (!state.revealed) return;
        if (state.revealPanelTimeout !== null) {
          window.clearTimeout(state.revealPanelTimeout);
        }
        state.revealPanelTimeout = window.setTimeout(() => {
          state.revealPanelTimeout = null;
          if (!state.revealed) return;
          elements.gameStage.dataset.revealStage = "panel";
          elements.resultPanel.classList.remove("hidden");
          elements.nextButton.classList.remove("hidden");
          guessMap.invalidateSize();
        }, 2000);
      }
    : undefined;
  guessMap.showAnswer(
    { lat: answer.lat, lng: answer.lng },
    { dramatic: dramaticReveal, onLineComplete }
  );
  hotspotOverlay.setVisible(false);

  // Duel presence: drop the opponent's pin alongside the answer so the
  // player sees exactly where their friend landed for this round. The
  // pin is purple to distinguish from your cyan guess and the orange
  // answer, and carries the opponent's name as a permanent label.
  if (
    state.session?.mode === "duel" &&
    state.duelOpponent &&
    state.currentRound
  ) {
    const oppGuess = state.duelOpponentGuesses[state.currentRound.id];
    if (oppGuess && oppGuess.lat != null && oppGuess.lng != null) {
      const oppName = state.duelOpponent.playerName || "Friend";
      guessMap.showOpponentPin(
        { lat: oppGuess.lat, lng: oppGuess.lng },
        `${oppName} · ${oppGuess.score.toLocaleString()}`
      );
    }
  }

  // For text-only modes, surface the panorama on reveal as a delightful payoff.
  if (state.session && (state.session.mode === "eyewitness" || state.session.mode === "newspaper")) {
    elements.textStage.classList.add("hidden");
    if (answer.imageUrl) {
      void viewer.loadTexture(answer.imageUrl).then(() => {
        syncPanoramaTextureBudget([answer.imageUrl]);
      });
    }
  }

  if (!dramaticReveal) {
    elements.nextButton.classList.remove("hidden");
    elements.gameStage.dataset.revealStage = "panel";
    elements.resultPanel.classList.remove("hidden");
  }
}

function describeLocationOutcome(reveal: RevealPayload): string {
  if (reveal.score.sameCountry) {
    return `Right country (${reveal.answer.country})`;
  }

  if (reveal.score.sameContinent) {
    return `Same continent — ${reveal.answer.continent}`;
  }

  return reveal.score.guessContinent ? `Off continent (${reveal.score.guessContinent})` : "No pin";
}

function describeBonus(reveal: RevealPayload): string {
  // When the round scored 0 the bonuses were earned but penalties zeroed
  // the total. Showing "+300 continent" alongside "ROUND SCORE 0" reads
  // contradictory, so we surface the explanation instead of the chips.
  if (reveal.score.roundScore === 0) {
    const earned = reveal.score.countryBonus + reveal.score.continentBonus;
    return earned > 0 ? "Bonus offset by penalties" : "No score awarded";
  }
  const parts: string[] = [];
  if (reveal.score.countryBonus > 0) parts.push(`+${reveal.score.countryBonus} country`);
  if (reveal.score.continentBonus > 0) parts.push(`+${reveal.score.continentBonus} continent`);
  return parts.length === 0 ? "No bonuses" : parts.join("  ·  ");
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Stylized era label for the intro stamp. Year is intentionally omitted —
// showing it would spoil the round's answer.
const ERA_LABELS: Record<Era, string> = {
  ancient: "ANTIQUITY",
  medieval: "MIDDLE AGES",
  "early-modern": "EARLY MODERN",
  modern: "MODERN ERA"
};

let eraStampTimeout: number | null = null;
function showEraStamp(era: Era): void {
  elements.eraStamp.textContent = ERA_LABELS[era];
  elements.eraStamp.classList.remove("visible");
  // Force reflow so re-adding the class restarts the animation.
  void elements.eraStamp.offsetWidth;
  elements.eraStamp.classList.add("visible");
  if (eraStampTimeout !== null) window.clearTimeout(eraStampTimeout);
  eraStampTimeout = window.setTimeout(() => {
    elements.eraStamp.classList.remove("visible");
    eraStampTimeout = null;
  }, 2500);
}

// Bucket the reveal outcome and apply matching audio + visual reaction. The
// data attribute drives a CSS keyframe flash on the game stage; the audio uses
// the existing stinger / wahWah synths.
type RevealBucket = "bullseye" | "near" | "same-continent" | "off-continent";
function bucketForReveal(reveal: RevealPayload): RevealBucket {
  const km = reveal.score.distanceKm;
  if (km <= 50) return "bullseye";
  if (km <= 500) return "near";
  if (reveal.score.sameContinent) return "same-continent";
  return "off-continent";
}

function playRevealBucketReaction(reveal: RevealPayload): void {
  const bucket = bucketForReveal(reveal);
  elements.gameStage.dataset.revealBucket = bucket;
  // Clear after the keyframe finishes so a re-reveal can re-trigger.
  window.setTimeout(() => {
    if (elements.gameStage.dataset.revealBucket === bucket) {
      delete elements.gameStage.dataset.revealBucket;
    }
  }, 1600);

  switch (bucket) {
    case "bullseye":
      feedback.stingerPerfect();
      celebrateScore(Math.max(reveal.score.roundScore, 4500));
      break;
    case "near":
      feedback.stingerSilver();
      break;
    case "same-continent":
      feedback.stingerBronze();
      break;
    case "off-continent":
      feedback.wahWah();
      break;
  }
}

// Show the global-average benchmark line on the result panel ("you: 312 km ·
// global avg: 2,400 km · 1,247 plays"). Hidden if data is missing or the
// player didn't drop a pin.
function renderGlobalAvgLine(reveal: RevealPayload, hadGuess: boolean): void {
  const stats = reveal.globalStats;
  if (!stats || !hadGuess || stats.count <= 1) {
    elements.globalAvgLine.classList.add("hidden");
    return;
  }
  const you = formatDistance(reveal.score.distanceKm);
  const avg = formatDistance(stats.avgDistanceKm);
  const beat = reveal.score.distanceKm < stats.avgDistanceKm;
  elements.globalAvgLine.innerHTML =
    `<span class="gal-you">you: <strong>${you}</strong></span>` +
    `<span class="gal-sep">·</span>` +
    `<span class="gal-avg">global avg: <strong>${avg}</strong></span>` +
    `<span class="gal-sep">·</span>` +
    `<span class="gal-count">${stats.count.toLocaleString()} plays</span>`;
  elements.globalAvgLine.dataset.beat = String(beat);
  elements.globalAvgLine.classList.remove("hidden");
}

// End-of-session recap. Walks through the session's per-round results with a
// short visual flick on each entry — turns the static numbers screen into a
// little story of the run.
function renderEndRecap(): void {
  const rounds = state.perRound;
  if (!rounds.length) {
    elements.endRecap.classList.add("hidden");
    return;
  }
  const items = rounds
    .map((entry, index) => {
      const r = entry.reveal;
      const title = r.answer.title;
      const km = formatDistance(r.score.distanceKm);
      const yrs = formatYearsOff(r.score.yearsOff);
      const score = r.score.roundScore.toLocaleString();
      return (
        `<li class="recap-row" style="--delay: ${index * 220}ms">` +
        `<span class="recap-num">${index + 1}</span>` +
        `<span class="recap-title">${escapeHtml(title)}</span>` +
        `<span class="recap-stats">${km} · ${yrs}</span>` +
        `<span class="recap-score">${score}</span>` +
        `</li>`
      );
    })
    .join("");
  elements.endRecap.innerHTML = `<ol class="recap-list">${items}</ol>`;
  elements.endRecap.classList.remove("hidden");
}

function advanceRound(): void {
  if (state.revealPanelTimeout !== null) {
    window.clearTimeout(state.revealPanelTimeout);
    state.revealPanelTimeout = null;
  }
  if (state.introTimeout !== null) {
    window.clearTimeout(state.introTimeout);
    state.introTimeout = null;
    state.introActive = false;
  }
  if (isFinalRound()) {
    void openEndReport();
    return;
  }

  state.currentRoundIndex += 1;
  state.currentRound = state.session?.rounds[state.currentRoundIndex] ?? null;
  renderCurrentRound();
}

function startTimer(): void {
  resetTimer();
  const duration = state.session?.mode === "quickplay" ? ROUND_DURATION_MS_QUICKPLAY : ROUND_DURATION_MS_DEFAULT;
  state.timerDurationMs = duration;
  state.timerStart = performance.now();
  state.timerDeadline = state.timerStart + duration;
  // Detective mode starts every round at phase 1 (sound only). Other modes
  // are pinned to phase 4 so the curtain stays hidden and the multiplier
  // path bakes to 1.0 server-side.
  if (state.session?.mode === "detective") {
    setDetectivePhase(1);
  } else {
    setDetectivePhase(4);
  }
  tickTimer();
}

const DETECTIVE_PHASE_LABEL: Record<1 | 2 | 3 | 4, string> = {
  1: "Phase 1 · sound only",
  2: "Phase 2 · one detail",
  3: "Phase 3 · eyewitness",
  4: "Phase 4 · panorama"
};
const DETECTIVE_PHASE_MULT: Record<1 | 2 | 3 | 4, string> = {
  1: "×2.0 if you lock in now",
  2: "×1.5 if you lock in now",
  3: "×1.25 if you lock in now",
  4: "Panorama unlocked — no bonus"
};

function setDetectivePhase(phase: 1 | 2 | 3 | 4): void {
  state.detectivePhase = phase;
  const isDetective = state.session?.mode === "detective";
  if (!isDetective) {
    elements.detectiveCurtain.classList.add("hidden");
    elements.detectiveCurtain.removeAttribute("data-phase");
    elements.textStage.classList.remove("detective-overlay");
    return;
  }
  elements.detectiveCurtain.classList.remove("hidden");
  elements.detectiveCurtain.dataset.phase = String(phase);
  elements.detectivePhaseLabel.textContent = DETECTIVE_PHASE_LABEL[phase];
  elements.detectivePhaseMultiplier.textContent = DETECTIVE_PHASE_MULT[phase];

  // Phase 3 surfaces the eyewitness banner over the curtained panorama. The
  // text-stage element already exists (used by eyewitness/newspaper modes);
  // we co-opt it with a different visual treatment via .detective-overlay.
  if (phase === 3 && state.currentRound?.eyewitness) {
    elements.textEyebrow.textContent = "Eyewitness fragment";
    elements.textBody.textContent = state.currentRound.eyewitness;
    elements.textStage.classList.remove("hidden");
    elements.textStage.classList.add("detective-overlay");
  } else {
    elements.textStage.classList.remove("detective-overlay");
    if (state.session?.mode === "detective") {
      elements.textStage.classList.add("hidden");
    }
  }

  if (phase === 4) {
    feedback.modalThump();
  } else {
    feedback.zoomTick("in");
  }
}

function tickTimer(): void {
  const remainingMs = Math.max(0, state.timerDeadline - performance.now());
  elements.timerLabel.textContent = formatCountdown(remainingMs);
  document.documentElement.style.setProperty("--timer-progress", String(remainingMs / state.timerDurationMs));
  document.documentElement.dataset.timerDanger = String(remainingMs <= 15_000);

  // Detective mode: advance phase at 15s / 30s / 45s of elapsed time.
  // Phase 4 reveals the panorama for the final 15s; the player can lock in
  // earlier for a multiplier (handled server-side from the phase we send).
  if (state.session?.mode === "detective" && !state.revealed) {
    const elapsedMs = state.timerDurationMs - remainingMs;
    const nextPhase: 1 | 2 | 3 | 4 =
      elapsedMs >= 45_000 ? 4 : elapsedMs >= 30_000 ? 3 : elapsedMs >= 15_000 ? 2 : 1;
    if (nextPhase !== state.detectivePhase) {
      setDetectivePhase(nextPhase);
    }
  }

  // Last 15s warning beep — escalates in volume, pitch and tempo. Two phases:
  //   15s → 2s: linear ramp from intensity 0 → 0.85 (gentle alarm)
  //   2s → 0s: steep ramp from 0.85 → 2.0 (insane trill, ~25ms throttle)
  // The feedback service throttles internally so calling every frame is fine.
  if (remainingMs > 2_000 && remainingMs <= 15_000) {
    const intensity = (1 - (remainingMs - 2_000) / 13_000) * 0.85;
    feedback.timerCountdown(intensity);
  } else if (remainingMs > 0 && remainingMs <= 2_000) {
    const intensity = 0.85 + (1 - remainingMs / 2_000) * 1.15;
    feedback.timerCountdown(intensity);
  }

  if (remainingMs <= 0) {
    feedback.explosion();
    void submitGuess(true);
    return;
  }

  state.timerFrame = window.requestAnimationFrame(tickTimer);
}

function formatCountdown(remainingMs: number): string {
  const totalCs = Math.max(0, Math.floor(remainingMs / 10));
  const minutes = Math.floor(totalCs / 6000);
  const seconds = Math.floor((totalCs % 6000) / 100);
  const centis = totalCs % 100;
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(centis).padStart(2, "0")}`;
}

function resetTimer(): void {
  if (state.timerFrame !== null) {
    window.cancelAnimationFrame(state.timerFrame);
    state.timerFrame = null;
  }

  const duration = state.session?.mode === "quickplay" ? ROUND_DURATION_MS_QUICKPLAY : ROUND_DURATION_MS_DEFAULT;
  state.timerDurationMs = duration;
  elements.timerLabel.textContent = formatCountdown(duration);
  document.documentElement.style.setProperty("--timer-progress", "1");
  document.documentElement.dataset.timerDanger = "false";
}

function syncSubmitState(): void {
  if (state.revealed || state.introActive) {
    elements.submitButton.disabled = true;
    elements.submitButton.classList.remove("ready-pulse");
    return;
  }
  if (state.phase === "year") {
    elements.submitButton.disabled = false;
    // In year phase the submit always reads "Lock year" — pulse to invite the
    // tap so the player isn't stuck staring at the panorama wondering what's next.
    elements.submitButton.classList.add("ready-pulse");
  } else {
    const ready = Boolean(state.guess.latLng);
    elements.submitButton.disabled = !ready;
    elements.submitButton.classList.toggle("ready-pulse", ready);
  }
}

function enterYearPhase(): void {
  state.phase = "year";
  elements.gameStage.dataset.phase = "year";
  setMapCollapsed(false);
  elements.mapShow.classList.add("hidden");
  elements.submitButtonText.textContent = "Lock year";
  elements.timelineHint.textContent = "Pick a year, then lock it in to place your pin on the map.";
  syncSubmitState();
  refreshIcons();
}

function enterLocationPhase(): void {
  state.phase = "location";
  elements.gameStage.dataset.phase = "location";
  setMapCollapsed(false);
  elements.submitButtonText.textContent = "Submit guess";
  elements.timelineHint.textContent = "Drop a pin on the map. Tap the minimize icon to peek at the scene.";
  syncSubmitState();
  refreshIcons();
  feedback.modalOpen();
  window.requestAnimationFrame(() => guessMap.invalidateSize());
}

function setMapCollapsed(collapsed: boolean): void {
  if (state.phase !== "location") {
    elements.mapPanel.removeAttribute("data-collapsed");
    elements.mapShow.classList.add("hidden");
    return;
  }
  if (collapsed) {
    elements.mapPanel.dataset.collapsed = "true";
    elements.mapShow.classList.remove("hidden");
  } else {
    elements.mapPanel.removeAttribute("data-collapsed");
    elements.mapShow.classList.add("hidden");
    window.requestAnimationFrame(() => guessMap.invalidateSize());
  }
  refreshIcons();
}

function showStartCue(): void {
  const cue = elements.startCue;
  cue.classList.add("hidden");
  void cue.offsetWidth;
  cue.classList.remove("hidden");
  feedback.modalOpen();
  window.setTimeout(() => feedback.submit(), 760);
  window.setTimeout(() => cue.classList.add("hidden"), 1500);
}

const HIGH_STRIKER_RANKS: Array<{ min: number; label: string; icon: string }> = [
  { min: 0,      label: "Lost Tourist",            icon: "frown" },
  { min: 800,    label: "Wrong-Continent Club",    icon: "x" },
  { min: 1_700,  label: "Confused Cartographer",   icon: "pencil" },
  { min: 2_700,  label: "Armchair Adventurer",     icon: "home" },
  { min: 3_700,  label: "Compass-less Wanderer",   icon: "compass" },
  { min: 4_700,  label: "Backseat Navigator",      icon: "car" },
  { min: 5_700,  label: "Globe-Trotter Trainee",   icon: "plane" },
  { min: 6_700,  label: "Map-Reader-in-Training",  icon: "map" },
  { min: 7_700,  label: "Atlas Apprentice",        icon: "book" },
  { min: 8_500,  label: "Calendar Sleuth",         icon: "calendar" },
  { min: 9_400,  label: "Archive Scout",           icon: "search" },
  { min: 12_500, label: "Field Historian",         icon: "scroll" },
  { min: 15_500, label: "Pin-Drop Prodigy",        icon: "map-pin" },
  { min: 19_000, label: "World Whisperer",         icon: "globe" },
  { min: 22_000, label: "Master Chronocartographer", icon: "crown" }
];

const HIGH_STRIKER_CLIMB_MS = 200;
const HIGH_STRIKER_HOLD_MS = 1300;

function setHighStrikerIcon(iconName: string): void {
  // The puck owns the icon — replace its children to swap.
  elements.highStrikerPuck.replaceChildren();
  const fresh = document.createElement("i");
  fresh.id = "high-striker-icon";
  fresh.setAttribute("data-lucide", iconName);
  fresh.setAttribute("aria-hidden", "true");
  elements.highStrikerPuck.appendChild(fresh);
  elements.highStrikerIcon = fresh;
  createIcons({ icons });
}

function resetHighStriker(): void {
  elements.highStriker.removeAttribute("data-final");
  elements.highStrikerPuck.style.bottom = "0%";
  elements.highStrikerScore.textContent = "0";
  elements.highStrikerRank.textContent = "";
  elements.highStrikerPrefix.textContent = "";
  setHighStrikerIcon(HIGH_STRIKER_RANKS[0].icon);
}

function runHighStriker(targetScore: number): void {
  let finalIndex = 0;
  for (let i = 0; i < HIGH_STRIKER_RANKS.length; i++) {
    if (targetScore >= HIGH_STRIKER_RANKS[i].min) finalIndex = i;
  }
  const totalSteps = finalIndex + 1;
  const stepMs = HIGH_STRIKER_CLIMB_MS + HIGH_STRIKER_HOLD_MS;
  const totalDurationMs = totalSteps * stepMs;

  animateNumber(elements.highStrikerScore, 0, targetScore, totalDurationMs, (v) =>
    Math.round(v).toLocaleString()
  );

  const minPos = 6;
  const maxPos = 92;
  const positionFor = (index: number): number => {
    if (HIGH_STRIKER_RANKS.length <= 1) return maxPos;
    return minPos + (maxPos - minPos) * (index / (HIGH_STRIKER_RANKS.length - 1));
  };

  for (let i = 0; i <= finalIndex; i++) {
    const rank = HIGH_STRIKER_RANKS[i];
    window.setTimeout(() => {
      elements.highStrikerPuck.style.bottom = `${positionFor(i)}%`;
      elements.highStrikerRank.textContent = rank.label;
      setHighStrikerIcon(rank.icon);
      if (i === finalIndex) {
        elements.highStriker.dataset.final = "true";
        elements.highStrikerPrefix.textContent = "You achieved";
        feedback.streak();
      } else {
        feedback.zoomTick("in");
      }
    }, i * stepMs);
  }
}

function preloadNextRound(): void {
  const nextRound = state.session?.rounds[state.currentRoundIndex + 1];
  if (nextRound && nextRound.imageUrl) {
    void viewer.preloadTexture(nextRound.imageUrl).then(() => {
      syncPanoramaTextureBudget();
    });
  }
  syncPanoramaTextureBudget();
}

function syncPanoramaTextureBudget(extraUrls: Array<string | null | undefined> = []): void {
  const keepUrls = new Set<string>();
  for (const url of extraUrls) {
    if (url) keepUrls.add(url);
  }

  if (state.currentRound?.imageUrl) {
    keepUrls.add(state.currentRound.imageUrl);
  }

  const nextRound = state.session?.rounds[state.currentRoundIndex + 1];
  if (nextRound?.imageUrl) {
    keepUrls.add(nextRound.imageUrl);
  }

  viewer.retainTextures(keepUrls);
}

function isFinalRound(): boolean {
  return Boolean(state.session && state.currentRoundIndex >= state.session.rounds.length - 1);
}

function openSettings(): void {
  elements.settingsModal.classList.remove("hidden");
}

function closeSettings(): void {
  elements.settingsModal.classList.add("hidden");
}

// On page load, restore any session the player had in progress so a tab
// close / accidental navigation doesn't force them to start over. If the
// id is gone (server cleared, different account) we silently fall back to
// the start modal.
async function resumeActiveSessionIfAny(): Promise<void> {
  const sessionId = readActiveSession();
  if (!sessionId) return;
  // Don't override flows that take the player elsewhere on load.
  const params = new URLSearchParams(window.location.search);
  if (params.has("duel") || params.has("spectate")) return;
  try {
    const session = await getSession(sessionId);
    const completed = session.totals.roundsCompleted ?? 0;
    if (completed >= session.rounds.length) {
      clearActiveSession();
      return;
    }
    state.session = session;
    state.duelOpponent = session.opponent ?? null;
    state.duelOpponentGuesses = {};
    state.selectedMode = session.mode;
    state.selectedFilter = session.filter;
    state.currentRoundIndex = completed;
    state.currentRound = session.rounds[completed] ?? null;
    state.lastReveal = null;
    state.perRound = [];
    state.duelShareCode = null;
    stopSplashBackground();
    elements.startModal.classList.add("hidden");
    elements.endModal.classList.add("hidden");
    elements.scoreLabel.textContent = String(session.totals.totalScore);
    const range = TIMELINE_RANGES[session.filter];
    timeline.setRange(range.min, range.max, range.start);
    renderCurrentRound();
  } catch (error) {
    clearActiveSession();
    // eslint-disable-next-line no-console
    console.warn("Could not resume previous session:", error);
  }
}

function openStart(): void {
  resetTimer();
  state.themeAudioUrl = pickThemeAudioUrl();
  clearActiveSession();
  state.session = null;
  state.lastReveal = null;
  state.perRound = [];
  state.currentRound = null;
  state.duelOpponent = null;
  state.duelShareCode = null;
  elements.startModal.classList.remove("hidden");
  elements.resultPanel.classList.add("hidden");
  elements.textStage.classList.add("hidden");
  hotspotOverlay.clear();
  viewer.showFallback();
  viewer.retainTextures([]);
  void preloadSplashBackground();
  void refreshDailyStatus();
  syncActiveAudio({ disableOnFailure: false });
}

async function openEndReport(): Promise<void> {
  const totals = state.lastReveal?.totals;
  if (!totals || !state.session) {
    return;
  }

  elements.endTitle.textContent = classifyScore(totals.totalScore);
  elements.endScore.textContent = String(totals.totalScore);
  elements.endDistance.textContent = formatDistance(totals.totalDistanceKm);
  elements.endYears.textContent = formatYearsOff(totals.totalYearsOff);
  renderEndRecap();
  elements.duelShare.classList.add("hidden");
  state.duelShareCode = null;
  elements.emojiShare.textContent = buildEmojiShare();
  resetEmailForm();
  resetHighStriker();

  const canContinue =
    state.session.mode === "classic" || state.session.mode === "eyewitness" || state.session.mode === "newspaper";
  elements.continueButton.classList.toggle("hidden", !canContinue);
  await renderDuelOutcome();
  elements.endModal.classList.remove("hidden");
  syncActiveAudio({ disableOnFailure: false });
  window.setTimeout(() => runHighStriker(totals.totalScore), 380);
}

async function prefetchDuelOpponentGuesses(duelCode: string, sessionId: string): Promise<void> {
  try {
    const summary = await compareDuel(duelCode, sessionId);
    const map: Record<string, { lat: number | null; lng: number | null; year: number; score: number }> = {};
    for (const entry of summary.opponent.perRound) {
      map[entry.roundId] = {
        lat: entry.guessLat,
        lng: entry.guessLng,
        year: entry.guessYear,
        score: entry.score
      };
    }
    state.duelOpponentGuesses = map;
  } catch {
    // Quietly fail — duel works without opponent pins, just less personal.
  }
}

async function renderDuelOutcome(): Promise<void> {
  if (!state.session) {
    elements.duelResult.classList.add("hidden");
    return;
  }

  if (state.session.mode !== "duel" || !state.duelOpponent) {
    elements.duelResult.classList.add("hidden");
    return;
  }

  try {
    const summary = await compareDuel(state.session.duelCode ?? "", state.session.id);
    const youTotal = summary.you.perRound.reduce((sum, entry) => sum + entry.score, 0);
    const opponentTotal = summary.opponent.total;
    let verdictClass = "tie";
    let verdict = "Even match";
    if (youTotal > opponentTotal) {
      verdictClass = "win";
      verdict = "You won";
    } else if (youTotal < opponentTotal) {
      verdictClass = "loss";
      verdict = "They held the lead";
    }

    elements.duelResult.innerHTML = `
      <span class="verdict ${verdictClass}">${verdict}</span>
      <strong>${escapeHtml(summary.opponent.name)} · ${opponentTotal.toLocaleString()}</strong>
      <span>You · ${youTotal.toLocaleString()}</span>
    `;
    elements.duelResult.classList.remove("hidden");
  } catch (error) {
    elements.duelResult.classList.add("hidden");
  }
}

async function persistDisplayName(): Promise<void> {
  const next = elements.displayNameInput.value.trim();
  try {
    const saved = await setDisplayName(next);
    elements.displayNameInput.value = saved;
  } catch (error) {
    showError("Could not update name.");
  }
}

async function openLeaderboard(): Promise<void> {
  elements.leaderboardModal.classList.remove("hidden");
  await renderLeaderboard();
}

async function renderLeaderboard(): Promise<void> {
  try {
    const entries = await getLeaderboard({
      filter: state.selectedFilter,
      region: state.selectedRegion,
      limit: 25
    });
    elements.scoreList.replaceChildren(
      ...entries.map((entry, index) => {
        const item = document.createElement("li");
        item.innerHTML = `<span>#${index + 1}</span><strong>${escapeHtml(entry.name)}</strong><em>${entry.score.toLocaleString()}</em>`;
        return item;
      })
    );

    if (entries.length === 0) {
      const empty = document.createElement("li");
      empty.innerHTML = "<span>—</span><strong>No scores yet</strong><em>Be the first.</em>";
      elements.scoreList.replaceChildren(empty);
    }
  } catch (error) {
    showError("Leaderboard unavailable.");
  }
}

async function openAchievements(): Promise<void> {
  elements.achievementsModal.classList.remove("hidden");
  try {
    const list = await listAchievements();
    elements.achievementsList.replaceChildren(
      ...list.map((entry) => {
        const li = document.createElement("li");
        li.dataset.locked = String(!entry.unlockedAt);
        li.innerHTML = `
          <i data-lucide="${escapeHtml(entry.icon)}" aria-hidden="true"></i>
          <div>
            <strong>${escapeHtml(entry.title)}</strong>
            <span>${escapeHtml(entry.description)}</span>
            ${entry.unlockedAt ? `<em>Unlocked ${formatDate(entry.unlockedAt)}</em>` : ""}
          </div>
        `;
        return li;
      })
    );
    refreshIcons();
  } catch (error) {
    showError("Could not load achievements.");
  }
}

async function copyShare(): Promise<void> {
  const text = buildEmojiShare();
  try {
    await navigator.clipboard.writeText(text);
    elements.copyShareButton.querySelector("span")!.textContent = "Copied!";
    window.setTimeout(() => {
      elements.copyShareButton.querySelector("span")!.textContent = "Copy result";
    }, 1600);
  } catch (error) {
    showError("Clipboard blocked. Long-press to copy manually.");
  }
}

async function downloadShareCard(): Promise<void> {
  if (!state.session || state.perRound.length === 0) {
    return;
  }

  try {
    const totals = state.lastReveal!.totals;
    const blob = await renderShareCard({
      title: state.session.mode === "daily" ? "LatLongAgo Daily" : "LatLongAgo",
      subtitle: state.session.mode === "daily" && state.daily?.date ? `Daily · ${state.daily.date}` : `${classifyScore(totals.totalScore)}`,
      totals,
      entries: state.perRound.map((entry) => ({
        guess: entry.guess.latLng,
        answer: { lat: entry.reveal.answer.lat, lng: entry.reveal.answer.lng },
        score: entry.reveal.score,
        round: entry.reveal.answer
      })),
      emoji: buildEmojiShare()
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `chronoscape-${(state.daily?.date ?? Date.now()).toString()}.png`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    showError("Could not save share card.");
  }
}

async function downloadReplayVideo(): Promise<void> {
  if (!state.session || state.perRound.length === 0) {
    return;
  }

  const button = elements.downloadVideoButton;
  const label = button.querySelector("span")!;
  const originalLabel = label.textContent ?? "Save replay";
  label.textContent = "Rendering…";
  button.disabled = true;
  try {
    const totals = state.lastReveal!.totals;
    const blob = await renderReplayVideo({
      title: state.session.mode === "daily" ? "LatLongAgo Daily" : "LatLongAgo",
      entries: state.perRound.map((entry) => ({
        guess: entry.guess.latLng,
        answer: { lat: entry.reveal.answer.lat, lng: entry.reveal.answer.lng },
        score: entry.reveal.score,
        round: entry.reveal.answer
      })),
      totalScore: totals.totalScore
    });
    if (!blob) {
      showError("Replay video not supported in this browser.");
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `chronoscape-replay-${(state.daily?.date ?? Date.now()).toString()}.webm`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    showError("Could not save replay.");
  } finally {
    label.textContent = originalLabel;
    button.disabled = false;
  }
}

async function openStats(): Promise<void> {
  elements.statsModal.classList.remove("hidden");
  try {
    const stats = await fetchPersonalStats();
    elements.statsSummary.innerHTML = `
      <div><span>Sessions</span><strong>${stats.totalSessions}</strong></div>
      <div><span>Best run</span><strong>${stats.bestSession.toLocaleString()}</strong></div>
      <div><span>Average</span><strong>${stats.averageScore.toLocaleString()}</strong></div>
      <div><span>Rounds</span><strong>${stats.totalRounds}</strong></div>
      <div><span>Streak</span><strong>${stats.currentStreak}d</strong></div>
      <div><span>ELO</span><strong>${stats.elo}</strong></div>
    `;

    const biasMax = Math.max(1, ...stats.guessHeatmap.map((entry) => entry.count));
    const pins = [
      ...stats.answerHeatmap.map((entry) => ({ ...entry, kind: "answer" as const })),
      ...stats.guessHeatmap.map((entry) => ({ ...entry, kind: "guess" as const }))
    ];
    elements.statsBias.replaceChildren(
      ...pins.map((entry) => {
        const dot = document.createElement("span");
        dot.className = entry.kind === "guess" ? "pin" : "pin answer";
        dot.style.left = `${((entry.lng + 180) / 360) * 100}%`;
        dot.style.top = `${((90 - entry.lat) / 180) * 100}%`;
        const intensity = entry.kind === "guess" ? Math.min(1, entry.count / biasMax) : 0.6;
        dot.style.opacity = String(0.35 + intensity * 0.65);
        return dot;
      })
    );

    elements.statsEras.replaceChildren(
      ...stats.byEra.map((row) => {
        const pct = row.total > 0 ? Math.round((row.seen / row.total) * 100) : 0;
        const wrapper = document.createElement("div");
        wrapper.className = "row";
        wrapper.innerHTML = `
          <span>${escapeHtml(row.era)} mastery</span>
          <div class="bar"><div class="fill" style="width:${pct}%"></div></div>
          <span>${row.seen}/${row.total}</span>
        `;
        return wrapper;
      })
    );

    // Stats panel inherits .stats-eras class which uses the `.row .bar` styles.
    elements.statsEras.classList.add("era-mastery");
    refreshIcons();
  } catch (error) {
    showError("Could not load stats.");
  }
}

async function openRecap(): Promise<void> {
  elements.recapModal.classList.remove("hidden");
  try {
    const recap = await fetchWeeklyRecap();
    const headline = recap.yourSessions === 0
      ? "You didn't play this week. The hardest scene was waiting."
      : `${recap.yourSessions} session${recap.yourSessions === 1 ? "" : "s"} · ${recap.yourScore.toLocaleString()} points`;

    elements.recapBody.innerHTML = `
      <p class="recap-headline">${escapeHtml(headline)}</p>
      <div class="stats-grid">
        <div><span>Your sessions</span><strong>${recap.yourSessions}</strong></div>
        <div><span>Your score</span><strong>${recap.yourScore.toLocaleString()}</strong></div>
        <div><span>Global plays</span><strong>${recap.globalSessions.toLocaleString()}</strong></div>
      </div>
      ${recap.yourBestRound
        ? `<p>Your best round: <strong>${escapeHtml(recap.yourBestRound.roundTitle)}</strong> · ${recap.yourBestRound.score.toLocaleString()} on ${escapeHtml(recap.yourBestRound.date)}.</p>`
        : ""}
      ${recap.hardestScene
        ? `<p>This week's hardest scene: <strong>${escapeHtml(recap.hardestScene.title)}</strong> (${escapeHtml(recap.hardestScene.place)}, ${escapeHtml(recap.hardestScene.dateLabel)}) — average score ${recap.hardestScene.averageScore.toLocaleString()}.</p>`
        : ""}
    `;
  } catch (error) {
    showError("Could not load recap.");
  }
}

async function openCollection(): Promise<void> {
  elements.collectionModal.classList.remove("hidden");
  try {
    const collection = await fetchCollection();
    elements.collectionStats.replaceChildren(
      ...[
        ["Seen", `${collection.seenCount} / ${collection.totalRounds}`],
        ["Ancient", eraFraction(collection.byEra.ancient)],
        ["Medieval", eraFraction(collection.byEra.medieval)],
        ["Early modern", eraFraction(collection.byEra["early-modern"])],
        ["Modern", eraFraction(collection.byEra.modern)]
      ].map(([label, value]) => {
        const stat = document.createElement("div");
        stat.className = "stat";
        stat.innerHTML = `<span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong>`;
        return stat;
      })
    );

    elements.collectionGrid.replaceChildren(
      ...collection.rounds.map((round) => {
        const cell = document.createElement("div");
        cell.className = `collection-cell ${round.seen ? "seen" : "locked"}`;
        if (round.seen && round.imageUrl) {
          cell.style.backgroundImage = `url(${round.imageUrl})`;
          cell.title = `${round.title} · ${round.place} · ${round.dateLabel}`;
          cell.innerHTML = `<span>${escapeHtml(round.dateLabel ?? "")}</span>`;
        } else {
          cell.title = `Locked · ${round.era}`;
        }
        return cell;
      })
    );

    updateCollectionLabel(collection.seenCount, collection.totalRounds);
  } catch (error) {
    showError("Could not load collection.");
  }
}

function eraFraction(stat: { seen: number; total: number }): string {
  return `${stat.seen} / ${stat.total}`;
}

function updateCollectionLabel(seen: number, total: number): void {
  const span = elements.openCollectionButton.querySelector("span");
  if (span) span.textContent = `Collection (${seen} / ${total})`;
}

async function openLeagues(): Promise<void> {
  elements.leaguesModal.classList.remove("hidden");
  await refreshLeagues();
}

async function refreshLeagues(): Promise<void> {
  try {
    const leagues = await fetchMyLeagues();
    if (leagues.length === 0) {
      elements.leaguesList.innerHTML = `<li><em>You're not in any leagues yet. Create one or paste a code.</em></li>`;
      elements.leagueDetail.classList.add("hidden");
      return;
    }

    elements.leaguesList.replaceChildren(
      ...leagues.map((league) => {
        const li = document.createElement("li");
        li.innerHTML = `
          <div>
            <strong>${escapeHtml(league.name)}</strong>
            <em>${league.members} member${league.members === 1 ? "" : "s"} · joined ${escapeHtml(formatDate(league.createdAt))}</em>
          </div>
          <code>${escapeHtml(league.code)}</code>
          <button type="button" data-action="open">Open</button>
        `;
        li.querySelector('[data-action="open"]')?.addEventListener("click", () => {
          void showLeagueDetail(league.code);
        });
        return li;
      })
    );
  } catch (error) {
    showError("Could not load leagues.");
  }
}

async function showLeagueDetail(code: string): Promise<void> {
  try {
    const detail = await fetchLeagueDetail(code);
    elements.leagueDetail.classList.remove("hidden");
    const url = `${window.location.origin}${window.location.pathname}?league=${encodeURIComponent(code)}`;
    elements.leagueDetail.innerHTML = `
      <div class="copy-row">
        <span><strong>${escapeHtml(detail.name)}</strong> · ${detail.members} members</span>
        <code>${escapeHtml(detail.code)}</code>
        <button type="button" data-action="copy" class="link-button">Copy invite</button>
      </div>
      <ol>
        ${detail.rows.map((row, index) => `
          <li>
            <span><strong>#${index + 1}</strong> ${escapeHtml(row.username ?? row.name)}</span>
            <span><em>${row.totalScore.toLocaleString()}</em> · best ${row.bestScore.toLocaleString()}</span>
          </li>
        `).join("")}
      </ol>
    `;
    elements.leagueDetail.querySelector('[data-action="copy"]')?.addEventListener("click", () => {
      void navigator.clipboard.writeText(url);
    });
  } catch (error) {
    showError("Could not load league detail.");
  }
}

async function handleCreateLeague(): Promise<void> {
  const name = elements.leagueNameInput.value.trim();
  if (!name) return;
  try {
    const league = await createLeague(name);
    elements.leagueNameInput.value = "";
    await refreshLeagues();
    await showLeagueDetail(league.code);
  } catch (error) {
    showError("Could not create league.");
  }
}

async function handleJoinLeague(): Promise<void> {
  const code = elements.leagueJoinInput.value.trim().toUpperCase();
  if (!code) return;
  try {
    await joinLeague(code);
    elements.leagueJoinInput.value = "";
    await refreshLeagues();
    await showLeagueDetail(code);
  } catch (error) {
    showError("Could not join league. Check the code.");
  }
}

async function openProfile(): Promise<void> {
  elements.profileModal.classList.remove("hidden");
  setProfileStatus("", "");
  try {
    const username = await fetchUsername();
    elements.usernameInput.value = username ?? "";
    if (username) {
      const url = `${window.location.origin}${window.location.pathname}p/${encodeURIComponent(username)}`;
      elements.profileLink.classList.remove("hidden");
      elements.profileLink.innerHTML = `
        <span>Public profile:</span>
        <code>/p/${escapeHtml(username)}</code>
        <button type="button" data-action="copy" class="link-button">Copy link</button>
      `;
      elements.profileLink.querySelector('[data-action="copy"]')?.addEventListener("click", () => {
        void navigator.clipboard.writeText(url);
      });
    } else {
      elements.profileLink.classList.add("hidden");
    }
  } catch (error) {
    setProfileStatus("error", "Could not load username.");
  }
}

async function handleClaimUsername(): Promise<void> {
  const desired = elements.usernameInput.value.trim();
  if (!desired) return;
  try {
    const claimed = await claimUsername(desired);
    setProfileStatus("ok", `Claimed /p/${claimed}`);
    void openProfile();
  } catch (error) {
    setProfileStatus("error", error instanceof Error ? error.message : "Could not claim username.");
  }
}

function setProfileStatus(state: "ok" | "error" | "", message: string): void {
  elements.usernameStatus.dataset.state = state;
  elements.usernameStatus.textContent = message;
}

async function continueCurrentSession(): Promise<void> {
  if (!state.session) return;
  try {
    const extended = await continueSession(state.session.id);
    state.session = extended;
    state.currentRoundIndex = extended.totals.roundsCompleted;
    state.currentRound = extended.rounds[state.currentRoundIndex] ?? null;
    elements.endModal.classList.add("hidden");
    if (state.currentRound) {
      showStartCue();
      renderCurrentRound();
    }
  } catch (error) {
    showError("Could not continue.");
  }
}

const STREAK_MILESTONES = new Set([7, 30, 100]);

function maybeShowStreakMilestone(reveal: RevealPayload): void {
  if (!STREAK_MILESTONES.has(reveal.streak.currentStreak)) return;

  elements.streakTitle.textContent = `Day ${reveal.streak.currentStreak}!`;
  elements.streakBody.innerHTML = `
    You've played the daily for <strong>${reveal.streak.currentStreak} days running</strong>.
    Longest streak: ${reveal.streak.longestStreak}. Tell people, they'll be jealous.
  `;
  elements.streakCelebration.classList.remove("hidden");
  refreshIcons();
}

async function copyStreakShare(): Promise<void> {
  if (!state.lastReveal) return;
  const days = state.lastReveal.streak.currentStreak;
  const text = `I'm on a ${days}-day LatLongAgo streak. Five historical scenes a day, one attempt each: latlongago.com`;
  try {
    await navigator.clipboard.writeText(text);
    elements.streakShareButton.querySelector("span")!.textContent = "Copied!";
    window.setTimeout(() => {
      elements.streakShareButton.querySelector("span")!.textContent = "Copy share text";
    }, 1600);
  } catch {
    showError("Clipboard blocked.");
  }
}

const ONBOARDING_KEY = "chronoscape.onboarded.v1";

const ONBOARDING_STEPS: Array<{
  selector: string | null;
  title: string;
  body: string;
  position: "center" | "top" | "bottom" | "left" | "right";
}> = [
  {
    selector: null,
    title: "Welcome",
    body: "LatLongAgo is a historical guessing game. Each round shows a 360° scene from history. You decide where it is and what year it was. Five rounds per session.",
    position: "center"
  },
  {
    selector: "#panorama-viewer",
    title: "Inspect the scene",
    body: "Click and drag the panorama to look around. Mouse wheel zooms in. Click the cyan + markers (where present) for short historical factoids.",
    position: "right"
  },
  {
    selector: "#map-panel",
    title: "Place a pin",
    body: "Click anywhere on the map to drop your guess for where this scene happened. Country and continent bonuses give you partial credit when you're close.",
    position: "left"
  },
  {
    selector: ".timeline-panel",
    title: "Pick a year",
    body: "Drag to scrub. Hold Shift while dragging for finer control. Click the year label to type a year directly. Older eras forgive larger errors than modern ones.",
    position: "top"
  },
  {
    selector: ".action-bar",
    title: "Wager and submit",
    body: "Set 1×, 2×, or 3× confidence before submitting. Speed earns a small bonus. Hints cost score. Click submit when ready.",
    position: "top"
  }
];

let onboardingStep = 0;

function showOnboardingStep(): void {
  const step = ONBOARDING_STEPS[onboardingStep];
  if (!step) {
    endOnboarding();
    return;
  }

  elements.onboardingOverlay.classList.remove("hidden");
  elements.onboardingStepLabel.textContent = `Step ${onboardingStep + 1} of ${ONBOARDING_STEPS.length}`;
  elements.onboardingStepTitle.textContent = step.title;
  elements.onboardingStepBody.textContent = step.body;

  document.querySelectorAll(".onboarding-spotlight").forEach((node) => node.remove());

  if (step.selector) {
    const target = document.querySelector(step.selector) as HTMLElement | null;
    if (target) {
      const rect = target.getBoundingClientRect();
      const spot = document.createElement("div");
      spot.className = "onboarding-spotlight";
      spot.style.left = `${rect.left - 8}px`;
      spot.style.top = `${rect.top - 8}px`;
      spot.style.width = `${rect.width + 16}px`;
      spot.style.height = `${rect.height + 16}px`;
      elements.onboardingOverlay.append(spot);

      placeOnboardingTooltip(rect, step.position);
    }
  } else {
    elements.onboardingTooltip.style.left = `50%`;
    elements.onboardingTooltip.style.top = `50%`;
    elements.onboardingTooltip.style.transform = `translate(-50%, -50%)`;
  }

  elements.onboardingBack.style.visibility = onboardingStep === 0 ? "hidden" : "visible";
  elements.onboardingNext.querySelector("span")!.textContent =
    onboardingStep === ONBOARDING_STEPS.length - 1 ? "Got it" : "Next";
  refreshIcons();
}

function placeOnboardingTooltip(rect: DOMRect, position: typeof ONBOARDING_STEPS[number]["position"]): void {
  const tooltip = elements.onboardingTooltip;
  tooltip.style.transform = "";
  const padding = 16;
  switch (position) {
    case "right":
      tooltip.style.left = `${rect.right + padding}px`;
      tooltip.style.top = `${Math.max(40, rect.top + rect.height / 2 - 80)}px`;
      break;
    case "left":
      tooltip.style.left = `${Math.max(16, rect.left - 360 - padding)}px`;
      tooltip.style.top = `${Math.max(40, rect.top + rect.height / 2 - 80)}px`;
      break;
    case "top":
      tooltip.style.left = `${Math.max(16, rect.left + rect.width / 2 - 180)}px`;
      tooltip.style.top = `${Math.max(40, rect.top - 220 - padding)}px`;
      break;
    case "bottom":
      tooltip.style.left = `${Math.max(16, rect.left + rect.width / 2 - 180)}px`;
      tooltip.style.top = `${rect.bottom + padding}px`;
      break;
    default:
      tooltip.style.left = "50%";
      tooltip.style.top = "50%";
      tooltip.style.transform = "translate(-50%, -50%)";
  }
}

function advanceOnboarding(direction: number): void {
  const next = onboardingStep + direction;
  if (next >= ONBOARDING_STEPS.length) {
    endOnboarding();
    return;
  }
  if (next < 0) return;
  onboardingStep = next;
  showOnboardingStep();
}

function endOnboarding(): void {
  elements.onboardingOverlay.classList.add("hidden");
  document.querySelectorAll(".onboarding-spotlight").forEach((node) => node.remove());
  window.localStorage.setItem(ONBOARDING_KEY, "done");
}

async function refreshEventBanner(): Promise<void> {
  try {
    const events = await fetchEvents();
    const active = events.find((event) => event.active);
    if (!active) {
      elements.eventBanner.classList.add("hidden");
      return;
    }

    elements.eventBanner.classList.remove("hidden");
    elements.eventBanner.innerHTML = `
      <strong>${escapeHtml(active.name)} is live</strong>
      <span>${escapeHtml(active.description)}</span>
      <small>${active.daysRemaining} day${active.daysRemaining === 1 ? "" : "s"} remaining · ${active.roundCount} rounds</small>
      <button type="button" data-event="${escapeHtml(active.slug)}">Play this event</button>
    `;
    elements.eventBanner.querySelector('button[data-event]')?.addEventListener("click", () => {
      void startEvent(active.slug);
    });
  } catch (error) {
    elements.eventBanner.classList.add("hidden");
  }
}

async function startEvent(slug: string): Promise<void> {
  try {
    resetTimer();
    const session = await startSession({
      mode: "event",
      filter: "all",
      eventSlug: slug
    });
    state.session = session;
    state.duelOpponent = session.opponent ?? null;
    state.currentRoundIndex = session.totals.roundsCompleted ?? 0;
    state.currentRound = session.rounds[state.currentRoundIndex] ?? null;
    state.lastReveal = null;
    state.perRound = [];
    state.duelShareCode = null;
    stopSplashBackground();
    elements.startModal.classList.add("hidden");
    elements.endModal.classList.add("hidden");
    elements.scoreLabel.textContent = String(session.totals.totalScore);
    const range = TIMELINE_RANGES[session.filter];
    timeline.setRange(range.min, range.max, range.start);
    showStartCue();
    renderCurrentRound();
  } catch (error) {
    showError("Could not start event.");
  }
}

async function openSpectatorFromUrl(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("spectate");
  if (!sessionId) return;

  try {
    const replay = await fetchReplay(sessionId);
    elements.spectatorTitle.textContent = `${replay.playerName}'s ${replay.mode} run`;
    elements.spectatorEyebrow.textContent = `${replay.totalScore.toLocaleString()} points`;
    elements.spectatorTotals.innerHTML = `
      <div><span>Mode</span><strong>${escapeHtml(replay.mode)}</strong></div>
      <div><span>Filter</span><strong>${escapeHtml(replay.filter)}</strong></div>
      <div><span>Total</span><strong>${replay.totalScore.toLocaleString()}</strong></div>
    `;
    elements.spectatorList.replaceChildren(
      ...replay.rounds.map((round, index) => {
        const li = document.createElement("li");
        li.innerHTML = `
          <span class="rank">${index + 1}</span>
          <div>
            <strong>${escapeHtml(round.answer.title)}</strong>
            <em>${escapeHtml(round.answer.place)} · ${escapeHtml(round.answer.dateLabel)}</em>
            <em>${formatDistance(round.distanceKm)} · ${formatYearsOff(round.yearsOff)}</em>
          </div>
          <span class="score">${round.score.toLocaleString()}</span>
        `;
        return li;
      })
    );
    elements.spectatorPlayButton.onclick = () => {
      elements.spectatorModal.classList.add("hidden");
      const url = new URL(window.location.href);
      url.searchParams.delete("spectate");
      window.history.replaceState({}, "", url.toString());
      openStart();
    };
    elements.spectatorClose.onclick = () => elements.spectatorModal.classList.add("hidden");
    elements.spectatorCloseScrim.onclick = () => elements.spectatorModal.classList.add("hidden");
    elements.spectatorModal.classList.remove("hidden");
  } catch (error) {
    showError("Could not load spectator session.");
  }
}

async function copySpectatorLink(): Promise<void> {
  if (!state.session || !state.session.finished) {
    showError("Finish the run first.");
    return;
  }

  const url = `${window.location.origin}${window.location.pathname}?spectate=${state.session.id}`;
  try {
    await navigator.clipboard.writeText(url);
    const span = elements.copySpectatorButton.querySelector("span")!;
    const original = span.textContent ?? "Spectator link";
    span.textContent = "Copied!";
    window.setTimeout(() => {
      span.textContent = original;
    }, 1600);
  } catch {
    showError("Clipboard blocked.");
  }
}

async function createDuelLink(): Promise<void> {
  if (!state.session) {
    return;
  }

  try {
    const duel = await createDuel(state.session.id);
    state.duelShareCode = duel.code;
    elements.duelShare.classList.remove("hidden");
    const url = buildDuelShareUrl(duel.code);
    elements.duelShare.innerHTML = `
      <span>Send this link to a friend:</span>
      <code>${escapeHtml(duel.code)}</code>
      <button type="button" data-action="copy">Copy link</button>
    `;
    elements.duelShare.querySelector('[data-action="copy"]')?.addEventListener("click", () => {
      void navigator.clipboard.writeText(url);
    });
  } catch (error) {
    showError("Could not create duel.");
  }
}

function buildEmojiShare(): string {
  if (state.perRound.length === 0 || !state.session) {
    return "";
  }

  const placeIcons = ["⬜", "🟫", "🟨", "🟦", "🟩"];
  const yearIcons = ["⬜", "🟫", "🟨", "🟧", "🟥"];
  const lines = state.perRound.map((entry) => {
    const place = tierFromDistance(entry.reveal.score.distanceKm);
    const year = tierFromYears(entry.reveal.score.yearsOff);
    return `${placeIcons[place] ?? "⬜"}${yearIcons[year] ?? "⬜"}`;
  });

  const totals = state.lastReveal?.totals.totalScore ?? 0;
  const heading =
    state.session.mode === "daily" && state.daily?.date
      ? `LatLongAgo Daily ${state.daily.date} · ${totals.toLocaleString()}`
      : `LatLongAgo · ${totals.toLocaleString()}`;
  return [heading, ...lines, "play at /daily"].join("\n");
}

function tierFromDistance(distanceKm: number): number {
  if (distanceKm <= 25) return 4;
  if (distanceKm <= 200) return 3;
  if (distanceKm <= 1000) return 2;
  if (distanceKm <= 3000) return 1;
  return 0;
}

function tierFromYears(yearsOff: number): number {
  if (yearsOff === 0) return 4;
  if (yearsOff <= 5) return 3;
  if (yearsOff <= 25) return 2;
  if (yearsOff <= 100) return 1;
  return 0;
}

function renderRoundDots(total: number, activeIndex: number): void {
  elements.roundDots.replaceChildren(
    ...Array.from({ length: total }, (_, index) => {
      const dot = document.createElement("span");
      dot.className = "round-dot";
      dot.dataset.active = String(index === activeIndex);
      dot.dataset.done = String(index < activeIndex);
      // Running tally: completed rounds show their per-round score above the dot.
      const completed = state.perRound[index];
      if (completed) {
        const score = completed.reveal.score.roundScore;
        const label = document.createElement("strong");
        label.className = "round-dot-score";
        label.textContent = score.toLocaleString();
        if (score >= 4500) label.dataset.tier = "gold";
        else if (score >= 3500) label.dataset.tier = "silver";
        else if (score >= 2000) label.dataset.tier = "bronze";
        dot.appendChild(label);
      }
      return dot;
    })
  );
}

function classifyScore(score: number): string {
  if (score >= 22_000) {
    return "LatLongAgo Master";
  }

  if (score >= 16_000) {
    return "Field Historian";
  }

  if (score >= 9_000) {
    return "Archive Scout";
  }

  return "New Researcher";
}

function refreshIcons(): void {
  // Lucide replaces nodes that have `data-lucide`. Re-run on dynamic content.
  createIcons({ icons });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function showError(message: string): void {
  elements.roundTitle.textContent = message;
}

function requiredElement<T extends HTMLElement = HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing element: ${selector}`);
  }

  return element;
}

// Surface an unused helper to keep tree-shaker happy when type-checking.
void formatYear;

void initialize();
